// ChainFlow — global app store
// Lightweight provider that keeps contacts, properties and deals in
// localStorage so the app works without a backend. Re-hydration is
// version-tagged so we can ship breaking schema changes safely.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  sampleContacts,
  sampleProperties,
  sampleDeals,
  sampleDocuments,
  FOLDERS,
} from "./sampleData";
/* iter-alignment · P54 — Live API SSOT for deals & properties.
 * All workspace state (deals, properties) is loaded from
 * `/api/deals` and `/api/properties` and mapped through the
 * `dealFromApi` / `propertyFromApi` transforms. Pilot fixtures
 * (`PILOT_DEALS`, `PILOT_STORE_PROPERTIES`) are no longer imported
 * or merged here — they live in `pilotData.js` for test fixtures
 * only.  The old `PILOT_DEAL_DOC_REQUESTS` seed is retired too;
 * document requests are user-created and persisted via the same
 * `PILOT_DOC_REQUESTS_KEY` localStorage bucket (compatibility
 * overlay — see `applyPilotOverrides` for scope + rationale). */
import { useAuth } from "./authStore";
import { api } from "../lib/api";
/* iter-282 — Documents → Timeline automation engine. Pure helpers
 * (see /app/frontend/src/data/docTimelineAutomation.js). Used by
 * fulfillPilotDocumentRequest + addDocument to auto-advance
 * milestones, clear matching blockers and log activity when key
 * documents arrive. Flag-gated via
 * `REACT_APP_DOC_TIMELINE_AUTOMATION_V1`. */
import {
  automationPlanFor,
  isDocTimelineAutomationEnabled,
} from "./docTimelineAutomation";
/* iter-294 — Canonical deal-health resolver. chainStore uses it to
 * derive `deal.status` so the field never diverges from the band
 * shown in Deal Rooms / Forecasting Centre / Deal Command / header
 * pill. Pure function — safe to call from the migration path. */
import { dealStatusFor } from "./dealHealth";
/* iter-284 — Document chasing engine. T+3 / T+5 / T+7 chase
 * ladder, deterministic from request.created_at. Flag-gated via
 * `REACT_APP_DOC_CHASING_V1`. */
import {
  getChaseStateFor,
  chaseActivityRowFor,
  isDocChasingEnabled,
} from "./docChasing";
/* iter-285 — Participant accountability engine. Decorates
 * activity rows with `actor_participant_role` and provides the
 * participant-signal envelope consumed by the iter-281 scorer.
 * Flag-gated via `REACT_APP_PARTICIPANT_ACCOUNTABILITY_V1`. */
import {
  decorateActivityWithParticipant,
  isParticipantAccountabilityEnabled,
} from "./participants";

// ─── Schema constants ────────────────────────────────────────────────────────
// 12-stage canonical deal pipeline (per product spec).
export const DEAL_STAGES = [
  { id: "offer",          label: "Offer Accepted" },
  { id: "aml",            label: "AML / ID Checks" },
  { id: "memo",           label: "Memorandum of Sale" },
  { id: "solicitors",     label: "Solicitors Instructed" },
  { id: "mortgage_app",   label: "Mortgage Application" },
  { id: "survey",         label: "Survey Booked" },
  { id: "searches",       label: "Searches Ordered" },
  { id: "enquiries",      label: "Enquiries Raised" },
  { id: "mortgage_offer", label: "Mortgage Offer Received" },
  { id: "exchange_ready", label: "Exchange Ready" },
  { id: "exchanged",      label: "Exchanged" },
  { id: "completed",      label: "Completed" },
];

// Backwards-compatible mapping from legacy stage IDs we used to ship.
const LEGACY_STAGE_MAP = {
  instruction: "offer",
  mortgage:    "mortgage_app",
  exchange:    "exchange_ready",
  completion:  "completed",
};

export const CONTACT_ROLES   = ["Buyer", "Seller", "Solicitor", "Mortgage Broker", "Estate Agent", "Managing Agent", "Surveyor", "Other"];
export const CONTACT_STATUS  = ["Active", "Pending", "Archived"];
export const PROPERTY_STATUS = ["Draft", "Listed", "Under Offer", "Sales Agreed", "Exchanged", "Completed", "Withdrawn"];
export const DEAL_STATUS     = ["Active", "At Risk", "Exchanged", "Completed", "Fallen Through"];
export const RISK_LEVEL      = ["Low", "Medium", "High"];
export const TENURES         = ["Freehold", "Leasehold", "Share of Freehold", "Commonhold"];

const PROPERTY_STATUS_LEGACY = {
  "Pre-launch":  "Draft",
  "Live":        "Listed",
  "Under offer": "Under Offer",
  "Sold STC":    "Sales Agreed",
};

// ─── Migration helpers ───────────────────────────────────────────────────────
const migrateContact = (c) => ({
  notes: "",
  tags: [],
  ...c,
  status: c.status || "Active",
});

const migrateProperty = (p) => ({
  notes: "",
  ...p,
  status: PROPERTY_STATUS_LEGACY[p.status] || p.status || "Draft",
});

const migrateDeal = (d) => {
  const stage = LEGACY_STAGE_MAP[d.stage] || d.stage || "offer";
  const stageIdx = DEAL_STAGES.findIndex((s) => s.id === stage);
  const progress = Math.round(((stageIdx + 1) / DEAL_STAGES.length) * 100);
  let derivedStatus = d.status;
  if (!derivedStatus) {
    if (stage === "completed")           derivedStatus = "Completed";
    else if (stage === "exchanged")      derivedStatus = "Exchanged";
    else {
      /* iter-294 — Canonical resolver replaces the legacy
       * `chainHealth < 70 → At Risk` heuristic. Every surface now
       * reads the same band from `dealStatusFor()` so the list
       * count, header pill, Exchange Eve chip, Deal Command hero
       * and Forecasting Centre cannot disagree about whether the
       * same deal is at risk. */
      const canon = dealStatusFor({ ...d, stage });
      derivedStatus = canon.band === "at_risk" ? "At Risk" : "Active";
    }
  }
  return {
    risk: (d.chainHealth ?? 100) >= 80 ? "Low" : (d.chainHealth ?? 100) >= 60 ? "Medium" : "High",
    fee: d.fee ?? null,
    name: d.name || null,
    ...d,
    stage,
    progress: d.progress ?? progress,
    status: derivedStatus,
  };
};

// ─── localStorage hydration ──────────────────────────────────────────────────
// v3 (iter-258) — unified 10-deal London workspace seed (Putney →
// Islington). Bumping the version invalidates any v2 persisted state
// so the new seed lands cleanly on existing demo browsers.
const STORAGE_VERSION = "v3";
const STORAGE_KEY = `chainflow:${STORAGE_VERSION}`;

// iter-alignment · P54 — Live API SSOT.
// All deals + properties come from `/api/deals` and `/api/properties`
// — the operator's workspace is always what the backend says it is.
// User-created rows are persisted the same way (via `POST /api/deals`
// and `POST /api/properties`) so there is no localStorage mirror to
// keep in sync.  Only the pilot-override + doc-request buckets
// (`PILOT_OVERRIDES_KEY` / `PILOT_DOC_REQUESTS_KEY`) survive as
// TEMPORARY client-side compatibility overlays for actions whose
// backend persistence has not been confirmed yet — see
// `applyPilotOverrides` for the exact scope.
// iter-270F — pilot deal overrides. Stores stage / milestone / activity
// patches (currently: milestoneStates · completedSteps · document
// tombstones / replacements · blocker resolutions · calm activity
// rows) on top of the API SSOT so surfaces that don't yet have a
// dedicated PATCH endpoint still persist across refresh.
const PILOT_OVERRIDES_KEY = "chainflow:pilotOverrides:v1";
// iter-270I — Document request lifecycle store (user-created only).
// Records live per-deal so the Documents Hub "Pending Requests"
// lane, the Deal Room activity feed and the Current Stage
// Request/Chase buttons all read from the same list.
const PILOT_DOC_REQUESTS_KEY = "chainflow:pilotDocRequests:v1";

// iter-290 — Client-side property archive ledger. Pilot operators
// hide duplicate / test / cancelled / fall-through transactions
// without losing data. Archived ids live in localStorage so the
// hide-from-active-views behaviour survives refresh. NO backend
// mutation — Deal Rooms, documents, activity, participants and
// forecasting history remain intact and reachable by direct URL.
const PILOT_ARCHIVED_PROPS_KEY = "chainflow:archivedPropertyIds:v1";
const loadArchivedPropertyIds = () => {
  try {
    const raw = localStorage.getItem(PILOT_ARCHIVED_PROPS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch (_e) { return []; }
};
const saveArchivedPropertyIds = (ids) => {
  try { localStorage.setItem(PILOT_ARCHIVED_PROPS_KEY, JSON.stringify(ids || [])); }
  catch (_e) { /* quota / private mode — silent */ }
};

// iter-270F — pilot overrides r/w + apply.
const loadPilotOverrides = () => {
  try {
    const raw = localStorage.getItem(PILOT_OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) { return {}; }
};
const savePilotOverrides = (obj) => {
  try { localStorage.setItem(PILOT_OVERRIDES_KEY, JSON.stringify(obj || {})); }
  catch (_e) { /* silent */ }
};
/* iter-alignment · P54 — Temporary compatibility overlay on top of
 * the API SSOT.  The API is the single source of truth for deals;
 * this overlay ONLY layers side-effects of client-only actions whose
 * dedicated backend PATCH endpoint has not yet been confirmed.
 *
 * Currently in scope (each with a matching TODO for the backend
 * replacement):
 *
 *   • `documents` + `removedDocIds` — pilot document uploads,
 *     replacements + deletions (Documents Part 3).
 *     TODO(P54.a): `POST /api/deals/{id}/documents` +
 *     `DELETE /api/deals/{id}/documents/{doc_id}` cover this,
 *     but the pilot "Replace" and tombstone flow still route
 *     through here so uploads made against a seed doc survive
 *     refresh.  Sunset when replacements land on the API.
 *
 *   • `milestoneStates` — inline milestone editing (iter-318).
 *     TODO(P54.b): add `PATCH /api/deals/{id}/stages/{stage}/milestones/{m}`.
 *
 *   • `completedSteps` — Mark Complete on the journey rail
 *     (iter-270F, iter-374/376).  TODO(P54.c): backend already
 *     exposes `POST /api/deals/{id}/journey/{step}/complete`;
 *     migrate the pilot helper to it once the cascade parity
 *     tests are green.
 *
 *   • `activity` — calm audit rows for pilot-only actions
 *     (uploads, chases, mark-complete cascades, property
 *     archive events).  TODO(P54.d): route through
 *     `POST /api/deals/{id}/activity`.
 *
 *   • `blockers` — pilot blocker resolutions cleared by the
 *     Documents → Timeline automation.  TODO(P54.e): add
 *     `POST /api/deals/{id}/blockers/{id}/resolve`.
 *
 * Everything else (stage, status, notes, prices, dates,
 * participants, favourites, etc.) is written through the API. */
const applyPilotOverrides = (deals) => {
  const overrides = loadPilotOverrides();
  if (!overrides || Object.keys(overrides).length === 0) return deals;
  return deals.map((d) => {
    const o = overrides[d.id];
    if (!o) return d;
    const baseActivity = Array.isArray(d.activity) ? d.activity : [];
    const newRows = Array.isArray(o.activity) ? o.activity : [];
    const baseDocs = Array.isArray(d.documents) ? d.documents : [];
    const overrideDocs = Array.isArray(o.documents) ? o.documents : [];
    const removedIds = new Set(Array.isArray(o.removedDocIds) ? o.removedDocIds : []);
    const overrideDocIds = new Set(overrideDocs.map((x) => x && x.id).filter(Boolean));
    const mergedDocs = [
      ...overrideDocs,
      ...baseDocs.filter((x) => x && !removedIds.has(x.id) && !overrideDocIds.has(x.id)),
    ];
    return {
      ...d,
      // Deliberately NOT layering stage / status / progress /
      // completionTarget from the overlay — those live on the API
      // SSOT.  Only side-effects of yet-to-be-persisted actions
      // are re-applied here (see docblock above for the exact
      // per-key scope).
      ...(Array.isArray(o.completedSteps) && o.completedSteps.length > 0
        ? { completedSteps: o.completedSteps }
        : {}),
      // Local-only list milestone edits (milestoneStates) are ignored: only saved data shows.
      documents: mergedDocs,
      activity: [...newRows, ...baseActivity],
      _pilotOverridden: true,
    };
  });
};
// iter-270I — pilot document requests r/w.
const loadPilotDocRequests = () => {
  try {
    const raw = localStorage.getItem(PILOT_DOC_REQUESTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) { return {}; }
};
const savePilotDocRequests = (obj) => {
  try { localStorage.setItem(PILOT_DOC_REQUESTS_KEY, JSON.stringify(obj || {})); }
  catch (_e) { /* silent */ }
};
// iter-alignment · P54 — Doc requests are user-created only; no seed.
const _docRequestsFor = (dealId, userMap) => {
  const user = (userMap && userMap[dealId]) || [];
  return [...user];
};

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
};

const savePersisted = (snapshot) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_e) {
    // localStorage may be unavailable (private mode, quota) — fail silently.
  }
};

const seedDealsWithDocs = sampleDeals.map((d) => ({
  ...d,
  documents: [...(d.documents || []), ...sampleDocuments.filter((doc) => doc.dealId === d.id)],
}));

const buildInitialState = () => {
  const persisted = loadPersisted();
  if (persisted && persisted.contacts && persisted.properties && persisted.deals) {
    return {
      properties: persisted.properties.map(migrateProperty),
      deals:      persisted.deals.map(migrateDeal),
      // iter-alignment · P54 — Favourites are user-toggled; defaults
      // are empty.  Persisted favourites (from earlier sessions)
      // still hydrate here so pilot operators don't lose them on
      // upgrade.
      favourites: new Set(persisted.favourites ?? []),
      favDocs:    new Set(persisted.favDocs    ?? sampleDocuments.filter((d) => d.favourite).map((d) => d.id)),
    };
  }
  return {
    properties: sampleProperties.map(migrateProperty),
    deals:      seedDealsWithDocs.map(migrateDeal),
    favourites: new Set(),
    favDocs:    new Set(sampleDocuments.filter((d) => d.favourite).map((d) => d.id)),
  };
};

// ─── Context ─────────────────────────────────────────────────────────────────
const ChainStoreContext = createContext(null);

export const useChainStore = () => {
  const ctx = useContext(ChainStoreContext);
  if (!ctx) throw new Error("useChainStore must be used within ChainStoreProvider");
  return ctx;
};

const id = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 8)}`;

// ─── API ↔ store transforms (snake_case ↔ camelCase) ────────────────────────
const propertyFromApi = (p) => ({
  id: p.id,
  org_id: p.org_id,
  address: p.address,
  postcode: p.postcode,
  locality: p.locality || null,
  type: p.type,
  beds: p.beds,
  baths: p.baths,
  price: p.price,
  tenure: p.tenure,
  epc: p.epc,
  svc: p.svc,
  rent: p.rent,
  status: p.status,
  sellerId: p.seller_id || null,
  agentId: p.agent_id || null,
  office: p.office,
  thumb: p.thumb,
  notes: p.notes,
  // iter-259 — Lightweight operational property metadata.
  parking: p.parking || null,
  parkingSpaces: p.parking_spaces ?? p.parkingSpaces ?? null,
  // iter-alignment · P0 · Property Edit → Detail View SSOT Refresh
  // (2026-02-19) — round-trip lease years so the Property detail
  // view + Edit drawer both read from the canonical SSOT.
  leaseYears: p.lease_years ?? p.leaseYears ?? null,
  /* iter-alignment · P3 · Properties · Commission Terms (2026-02-28)
   * Surface the Property SSOT's commission model so the Property
   * details view can render the live estimate AND the Deal Room
   * wizard can inherit these values verbatim on create. */
  commissionType:         p.commission_type          ?? null,
  commissionPct:          p.commission_pct           ?? null,
  fixedFee:               p.fixed_fee                ?? null,
  commissionVatIncluded:  p.commission_vat_included === undefined
                           ? undefined
                           : Boolean(p.commission_vat_included),
  /* iter-alignment · P0 · Property Edit Drawer — Compact Redesign
   * (2026-02-28) — Building Safety Act Yes/No flag lives on the
   * Property SSOT.  Optional / tri-state (undefined = unset). */
  bsaApplies:             p.bsa_applies === undefined || p.bsa_applies === null
                           ? undefined
                           : Boolean(p.bsa_applies),
  /* iter-alignment · P0 · Deal Room · Property Details Card
   * (2026-03-01) — Round-trip the new operational fields
   * surfaced by the right-rail Property Details card.  All
   * optional — undefined = unset. */
  groundRent:                 p.ground_rent                 ?? null,
  managingAgent:              p.managing_agent              ?? null,
  localAuthority:             p.local_authority             ?? null,
  councilTaxBand:             p.council_tax_band            ?? null,
  ews1Status:                 p.ews1_status                 ?? null,
  ews1Rating:                 p.ews1_rating                 ?? null,
  sharedOwnership:            p.shared_ownership === undefined || p.shared_ownership === null
                               ? undefined
                               : Boolean(p.shared_ownership),
  sharedOwnershipPercentage:  p.shared_ownership_percentage ?? null,
  leaseExtensionStatus:       p.lease_extension_status      ?? null,
  createdAt: p.created_at ? Date.parse(p.created_at) : Date.now(),
});

const propertyToApi = (p) => {
  const map = {
    sellerId: "seller_id",
    agentId: "agent_id",
    parkingSpaces: "parking_spaces",
    // iter-alignment · P0 · Property Edit → Detail View SSOT Refresh
    // (2026-02-19) — mirror snake-case for the backend.
    leaseYears: "lease_years",
    // iter-alignment · P3 · Properties · Commission Terms (2026-02-28)
    commissionType: "commission_type",
    commissionPct: "commission_pct",
    fixedFee: "fixed_fee",
    commissionVatIncluded: "commission_vat_included",
    // iter-alignment · P0 · Property Edit Drawer — Compact Redesign
    bsaApplies: "bsa_applies",
    // iter-alignment · P0 · Deal Room · Property Details Card (2026-03-01)
    groundRent: "ground_rent",
    managingAgent: "managing_agent",
    localAuthority: "local_authority",
    councilTaxBand: "council_tax_band",
    ews1Status: "ews1_status",
    ews1Rating: "ews1_rating",
    sharedOwnership: "shared_ownership",
    sharedOwnershipPercentage: "shared_ownership_percentage",
    leaseExtensionStatus: "lease_extension_status",
  };
  const SKIP = new Set(["id", "org_id", "created_at", "updated_at", "createdAt"]);
  // iter-alignment · P0 · Property Edit → Detail View SSOT Refresh
  // (2026-02-19) — integer-typed backend fields treat empty
  // strings as validation errors.  Coerce blank → null so the
  // drawer can send a partial payload without a 422.
  const INT_FIELDS = new Set(["parkingSpaces", "leaseYears", "beds", "baths", "sharedOwnershipPercentage"]);
  const out = {};
  for (const [k, v] of Object.entries(p || {})) {
    if (SKIP.has(k)) continue;
    let value = v;
    if (INT_FIELDS.has(k) && (value === "" || value === undefined)) value = null;
    out[map[k] || k] = value;
  }
  return out;
};

const dealFromApi = (d) => normaliseDealDates({
  id: d.id,
  org_id: d.org_id,
  name: d.name,
  propertyId: d.property_id,
  buyerId: d.buyer_id,
  sellerId: d.seller_id,
  agentId: d.agent_id,
  solicitorId: d.solicitor_id,
  buyerSolicitorId: d.buyer_solicitor_id,
  sellerSolicitorId: d.seller_solicitor_id,
  brokerId: d.broker_id,
  // iter-alignment · P51 — Aggregated contact_ids across every
  // participant channel (six FK columns + Postgres
  // `deal_participants.contact_id`).  `dealsForContact` reads
  // this so contacts added via non-FK slots (progressor / other)
  // still surface on their Contact Profile.
  participantContactIds: Array.isArray(d.participant_contact_ids)
    ? d.participant_contact_ids.filter(Boolean)
    : [],
  // iter-alignment · P0 · Participant SSOT Fanout (2026-02-28)
  // TYPED participant rows fanned out from Postgres
  // `deal_participants` on every list/detail response.  Every
  // surface that needs to resolve a role → contact (Deal Summary
  // Modal, Deal Room header chips, …) reads this slice instead
  // of firing a second `/api/v2/deals/{id}/participants` request.
  // Shape per row: `{contact_id, participant_type, full_name,
  // email, organisation_name}` — matches the backend `DealOut`
  // field verbatim; frontend consumers should never mutate this.
  participants: Array.isArray(d.participants) ? d.participants : [],
  stage: d.stage,
  status: d.status,
  risk: d.risk,
  agreedPrice: d.agreed_price,
  fee: d.fee,
  /* iter-alignment · P3 · Agreed Fee Persistence (2026-02-28)
   * Surface the canonical Revenue-card fields so the
   * FinancialsSheet re-hydrates with the saved commission type,
   * VAT flag, expected exchange month and risk band across every
   * refresh / navigation / background refetch.  Missing fields
   * fall through to `undefined` so the sheet's `_detectMode()`
   * fallback picks the right mode for legacy deals. */
  commissionType:        d.commission_type        ?? d.commissionType        ?? null,
  feePct:                d.fee_pct                ?? d.feePct                ?? null,
  vatIncluded:           (d.vat_included ?? d.vatIncluded) === undefined
                         ? undefined
                         : Boolean(d.vat_included ?? d.vatIncluded),
  expectedExchangeMonth: d.expected_exchange_month ?? d.expectedExchangeMonth ?? null,
  exchangedAt: d.exchanged_at ?? null,
  completedAt: d.completed_at ?? null,
  forecastConfidence:    d.forecast_confidence    ?? d.forecastConfidence    ?? null,
  revenueAtRisk:         d.revenue_at_risk        ?? d.revenueAtRisk         ?? null,
  targetExchange: d.target_exchange,
  targetCompletion: d.target_completion,
  // iter-alignment · Deal Room · Sale Agreed + Transaction Dates
  // (2026-03-02) — canonical Sale Agreed timestamp surfaced from
  // the backend `Deal.sale_agreed_date` column.  Nullable — the
  // Deal Room header renders `+ Add date` for legacy deals.
  saleAgreedDate: d.sale_agreed_date ?? d.saleAgreedDate ?? null,
  // iter-alignment · Deal Room · Final Right Rail · Buyer Position
  // (2026-03-02) — surface the six deal-level Buyer Position
  // columns for the Buyer Position card.  All nullable.
  buyerType:          d.buyer_type          ?? d.buyerType          ?? null,
  propertyToSell:     d.property_to_sell    ?? d.propertyToSell     ?? null,
  mortgagePosition:   d.mortgage_position   ?? d.mortgagePosition   ?? null,
  proofOfFunds:       d.proof_of_funds      ?? d.proofOfFunds       ?? null,
  depositAmount:      d.deposit_amount      ?? d.depositAmount      ?? null,
  depositPercentage:  d.deposit_percentage  ?? d.depositPercentage  ?? null,
  progress: d.progress,
  chainHealth: d.chain_health,
  notes: d.notes,
  // Transaction Structure surfaces (Create Deal → Deal Command).
  tenure:         d.tenure         || null,
  chainStatus:    d.chain_status   || null,
  mortgageStatus: d.mortgage_status || null,
  /* iter-306 — Surface the deal's finance type and Building Safety
   * Act applicability so the canonical journey resolver can render
   * dynamic milestones (skip Mortgage for cash buyers · honour an
   * explicit `applicableBSA` flag · default to BSA-on for
   * leasehold). Backwards-compatible: missing API fields fall
   * through to `undefined` so the resolver applies its defaults. */
  financeType:    d.finance_type   || d.financeType   || undefined,
  applicableBSA:  (d.applicable_bsa !== undefined ? d.applicable_bsa
                   : (d.applicableBSA !== undefined ? d.applicableBSA : undefined)),
  /* iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
   * (2026-03-05) — surface the canonical `Deal.funding_basis`
   * off the API envelope so `transactionJourney.optsForDeal`,
   * the Editable Funding Basis pill and the Buyer Position card
   * all reconcile the Mortgage rail visibility from a single
   * persisted signal.  Written by
   * `PATCH /api/v2/deals/{id}/promotion-baseline`; both v1
   * (`/api/deals`) and v2 (`/api/v2/deals/{id}`) responses now
   * carry it.  Also expose the camelCase alias every existing
   * consumer already reads so no downstream signature change is
   * needed. */
  funding_basis:  d.funding_basis  ?? d.fundingBasis  ?? null,
  fundingBasis:   d.funding_basis  ?? d.fundingBasis  ?? null,
  // Paired compat flag the Deal Room already reads; surfaced so the list rail resolves Mortgage identically.
  is_cash_buyer:  d.is_cash_buyer  ?? d.isCashBuyer   ?? null,
  isCashBuyer:    d.is_cash_buyer  ?? d.isCashBuyer   ?? null,
  chainPosition:  d.chain_position ?? d.chainPosition ?? null,
  archived: !!d.archived,
  favourite: !!d.favourite,
  invitations: d.invitations || {},
  /* iter-alignment · Broker Contact Profile Refinement (2026-02-28) —
   * Surface the canonical `lender` column (Deal.lender, populated
   * via `routes_deal_promotion._promote_deal`) so the Broker
   * profile row can render the lender name when available.  Falls
   * through to `null` for deals without a lender captured yet, so
   * the UI hides the field cleanly rather than fabricating one. */
  lender: d.lender || d.lender_name || null,
  documents: d.documents || [],
  messages: d.messages || [],
  activity: d.activity || [],
  /* P0 · Blocker Data Population (2026-02-28) — Surface the
   * canonical `deal.blockers` SSOT enriched by the backend
   * (`deal_blocker_enricher.enrich_blockers`).  Without this map
   * the frontend `filterActiveBlockers`, `_hasOutstandingBlocker`,
   * `dealHealth.scoreDeal`, and blocker-rail renderers see an
   * empty array on every deal even though the API delivers the
   * canonical detector output.  Empty for terminal deals. */
  blockers: Array.isArray(d.blockers) ? d.blockers : [],
  /* iter-alignment · Deep NBA Payload (2026-02-19).  Threads the
   * canonical `next_best_action` + `waiting_on` envelopes from
   * `/api/deals` onto the chainStore deal shape so every
   * consumer (Deal Rooms list, Command Centre overview,
   * Properties Quick View, future radars) reads the same
   * recommendation without synthesising fallbacks. */
  next_best_action: d.next_best_action ?? d.nextBestAction ?? null,
  waiting_on:       d.waiting_on       ?? d.waitingOn       ?? null,
  // Classification inputs (shared classifyDeal rule).
  waiting_days:     d.waiting_days ?? null,
  inactive_days:    d.inactive_days ?? null,
  pre_exchange_outstanding: d.pre_exchange_outstanding ?? null,
  waiting_days:     d.waiting_days     ?? d.waitingDays     ?? null,
  exchange_probability: d.exchange_probability ?? d.exchangeProbability ?? null,
  createdAt: d.created_at ? Date.parse(d.created_at) : Date.now(),
  /* iter-361 · F-08 · Live "Updated ..." on transaction cards —
   * surface the deal's canonical `updated_at` so PropertiesPage
   * card can render the real change timestamp instead of the
   * hard-coded "Updated just now" placeholder.  ISO string
   * preserved verbatim; consumers format on read. */
  updatedAt: d.updated_at || d.updatedAt || null,
  /* iter-361 · F-08 · Milestone progress on transaction cards —
   * completed_steps is derived server-side from milestone activity
   * rows.  Falls through to null so consumers can distinguish
   * "no milestones yet" from "0 completed of N". */
  completedStepsApi: Array.isArray(d.completed_steps) ? d.completed_steps
                     : (Array.isArray(d.completedSteps) ? d.completedSteps : null),
});

/* iter-275 — Canonical date-field normaliser.
 *
 * The pilot dataset uses `exchangeDate` / `completionDate`, the API
 * + Create Deal form use `targetExchange` / `targetCompletion`.
 * Without normalisation, components that read only one naming
 * style render "Not set" for the other half of the dataset
 * (P0 — date certainty is non-negotiable on a transaction OS).
 *
 * This helper guarantees BOTH field names always carry the same
 * value, so every reader — Deal Room header, Overview right rail,
 * Active Milestone strip, intel descriptors — sees the dates the
 * operator entered, regardless of which naming style they happen
 * to read. Idempotent · pure · safe to call repeatedly. */
export function normaliseDealDates(d) {
  if (!d || typeof d !== "object") return d;
  const ex =
    d.targetExchange    || d.target_exchange   || d.exchange_target ||
    d.exchangeDate      || d.exchange_date     || null;
  const co =
    d.targetCompletion  || d.target_completion || d.completion_target ||
    d.completionDate    || d.completion_date   || null;
  return {
    ...d,
    targetExchange:   ex,
    targetCompletion: co,
    // Keep the legacy pilot aliases populated so existing pilot
    // consumers (Forecasting Centre, contact rail, etc.) continue
    // to read the same values.
    exchangeDate:     ex,
    completionDate:   co,
  };
}

/* iter-275 — Canonical date accessors. Prefer these over reading
 * deal.targetExchange / deal.exchangeDate directly so the rest of
 * the app can stay naming-agnostic. */
export const getDealExchangeTarget = (d) =>
  d?.targetExchange || d?.target_exchange || d?.exchange_target ||
  d?.exchangeDate   || d?.exchange_date   || null;

export const getDealCompletionTarget = (d) =>
  d?.targetCompletion || d?.target_completion || d?.completion_target ||
  d?.completionDate   || d?.completion_date   || null;

const dealToApi = (d) => {
  const map = {
    propertyId: "property_id",
    buyerId: "buyer_id",
    sellerId: "seller_id",
    agentId: "agent_id",
    solicitorId: "solicitor_id",
    buyerSolicitorId: "buyer_solicitor_id",
    sellerSolicitorId: "seller_solicitor_id",
    brokerId: "broker_id",
    agreedPrice: "agreed_price",
    targetExchange: "target_exchange",
    targetCompletion: "target_completion",
    // iter-alignment · Deal Room · Sale Agreed + Transaction Dates
    // (2026-03-02) — canonical camelCase↔snake_case bridge for the
    // new `sale_agreed_date` column.  Same shape as the target
    // dates so `persistDateField("saleAgreedDate", iso)` writes
    // straight through to the deal PATCH surface.
    saleAgreedDate: "sale_agreed_date",
    // iter-alignment · Deal Room · Final Right Rail · Buyer Position
    // (2026-03-02) — deal-level SSOT bridge for the six Buyer
    // Position fields.  Same pattern as the target dates; writes
    // straight through the canonical deal PATCH surface.
    buyerType:          "buyer_type",
    propertyToSell:     "property_to_sell",
    mortgagePosition:   "mortgage_position",
    proofOfFunds:       "proof_of_funds",
    depositAmount:      "deposit_amount",
    depositPercentage:  "deposit_percentage",
    chainHealth: "chain_health",
    // Transaction Structure surfaces.
    chainStatus: "chain_status",
    mortgageStatus: "mortgage_status",
    // iter-alignment · P3 · Agreed Fee Persistence (2026-02-28)
    // The FinancialsSheet edits these Revenue-card fields — the
    // camelCase keys must map to the canonical snake_case columns
    // on the backend so the PATCH actually persists them.  Without
    // this map they were dropped silently by Pydantic's
    // `extra="ignore"` default, which is what caused the fee to
    // reset on every reload.
    commissionType: "commission_type",
    feePct: "fee_pct",
    vatIncluded: "vat_included",
    expectedExchangeMonth: "expected_exchange_month",
    forecastConfidence: "forecast_confidence",
    revenueAtRisk: "revenue_at_risk",
  };
  const SKIP = new Set(["id", "org_id", "created_at", "updated_at", "createdAt", "progress", "messages", "activity"]);
  const out = {};
  for (const [k, v] of Object.entries(d || {})) {
    if (SKIP.has(k)) continue;
    out[map[k] || k] = v;
  }
  return out;
};

export const ChainStoreProvider = ({ children }) => {
  const initial = useRef(buildInitialState()).current;
  const { user } = useAuth();
  const [contacts,       setContacts]       = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError,  setContactsError]  = useState(null);
  const [tasks,          setTasks]          = useState([]);
  const [notifications,  setNotifications]  = useState([]);
  const [viewings,       setViewings]       = useState([]);
  const [favourites,     setFavourites]     = useState(initial.favourites);
  const [favDocs,        setFavDocs]        = useState(initial.favDocs);
  const recentRef = useRef({ contacts: [], properties: [] });

  // Persist favourites only — tasks/notifications/viewings/contacts/properties/deals are server-side truth.
  useEffect(() => {
    savePersisted({
      contacts: [], properties: [], deals: [],
      favourites: Array.from(favourites),
      favDocs: Array.from(favDocs),
    });
  }, [favourites, favDocs]);

  // ─── Contacts (server-backed) ──────────────────────────────────────────────
  // Hydrate from /api/contacts whenever the authed user changes.
  useEffect(() => {
    if (!user) {
      setContacts([]);
      setContactsLoading(false);
      setContactsError(null);
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    setContactsError(null);
    api.get("/api/contacts")
      .then(({ data }) => {
        if (!cancelled) {
          setContacts(Array.isArray(data) ? data : []);
          setContactsLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setContactsError(e?.response?.data?.detail || e?.message || "Failed to load contacts");
          setContactsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Accepts either {first_name,last_name,...} (preferred) or legacy {name,...}.
  const addContact = useCallback(async (input) => {
    let { first_name, last_name } = input || {};
    if (!first_name && input?.name) {
      const parts = String(input.name).trim().split(/\s+/);
      first_name = parts[0] || "";
      last_name = parts.slice(1).join(" ");
    }
    const payload = {
      first_name: (first_name || "").trim(),
      last_name: (last_name || "").trim(),
      email: input?.email ? String(input.email).trim() : null,
      phone: (input?.phone || "").trim(),
      company: input?.company ? String(input.company).trim() : null,
      role: input?.role,
      notes: input?.notes || null,
      tags: Array.isArray(input?.tags) ? input.tags : [],
      pref: input?.pref || "Email",
      status: input?.status || "Active",
    };
    const { data } = await api.post("/api/contacts", payload);
    setContacts((arr) => [data, ...arr]);
    recentRef.current.contacts = [data.id, ...recentRef.current.contacts.filter((x) => x !== data.id)].slice(0, 5);
    return data;
  }, []);

  const updateContact = useCallback(async (cid, patch) => {
    const body = { ...(patch || {}) };
    if (body.name && !body.first_name && !body.last_name) {
      const parts = String(body.name).trim().split(/\s+/);
      body.first_name = parts[0] || "";
      body.last_name = parts.slice(1).join(" ");
    }
    delete body.name;
    delete body.id;
    delete body.org_id;
    delete body.created_at;
    delete body.updated_at;
    delete body.color;
    const { data } = await api.patch(`/api/contacts/${cid}`, body);
    setContacts((arr) => arr.map((c) => (c.id === cid ? data : c)));
    return data;
  }, []);

  const deleteContact = useCallback(async (cid) => {
    await api.delete(`/api/contacts/${cid}`);
    setContacts((arr) => arr.filter((c) => c.id !== cid));
  }, []);

  const refreshContacts = useCallback(async () => {
    if (!user) return;
    const { data } = await api.get("/api/contacts");
    setContacts(Array.isArray(data) ? data : []);
    return data;
  }, [user]);

  // ─── Properties (server-backed) ────────────────────────────────────────────
  // Hydrate from /api/properties whenever the authed user changes.
  const [properties,     setProperties]     = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [deals,          setDeals]          = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsHydrated, setDealsHydrated] = useState(false);

  /* iter-336/Δ — Ref to the latest `_maybeAutomateDocUpload`
   * closure. Initialised here so `appendPilotDocument` /
   * `replacePilotDocument` (both memoised with `[]` deps) can
   * route through `_automateDocUploadRef.current(...)` and call
   * the FRESH automation closure (which sees the latest `deals`
   * state). Assigned via the useEffect below `_maybeAutomateDocUpload`. */
  const _automateDocUploadRef = useRef(null);

  /* hydrateDeals — the single canonical workspace dataset loader.
   * Lives outside the useEffect so any surface (UpdateStageSheet
   * retry, post-seed event, post-clear-sample event, mobile Safari
   * re-foreground) can force a refetch with one call.
   *
   * iter-alignment · P54 — Live API SSOT.
   * iter-alignment · P0 · Pilot Deal-List Migration (2026-03-10) —
   * Repointed the primary deal-list read from the legacy v1
   * `/api/deals` (Mongo) to the canonical `/api/v2/deals`
   * (Postgres SSOT).  The v2 envelope was extended with the
   * property / participants / BSA / blocker projections v1 used to
   * emit so `dealFromApi` and every downstream consumer hydrate
   * unchanged.  Removes the last read-through Mongo dependency
   * that was capping the SPA at the 3 deals still living in the
   * v1 mirror. */
  const hydrateDeals = useCallback(async () => {
    if (!user) return [];
    setDealsLoading(true);
    try {
      const { data } = await api.get("/api/v2/deals");
      const rows = Array.isArray(data) ? data : [];
      const mapped = rows.map(dealFromApi);
      // iter-alignment · P0 · Pilot Workspace Reseed (2026-08-15)
      // — Postgres is now the SSOT for pilot deals; the client-
      // side `applyPilotOverrides` overlay has been retired.  The
      // API response IS the truth; no local documents / activity
      // / milestone overlays are re-layered here.
      const merged = mapped.map((d) => normaliseDealDates({ ...d }));
      setDeals(merged);
      return merged;
    } catch (_e) {
      setDeals([]);
      return [];
    } finally {
      setDealsLoading(false);
      setDealsHydrated(true);
    }
  }, [user]);

  /* refreshWorkspace — paired refetch of /api/properties + hydrateDeals.
   * iter-alignment · P54 — Live API SSOT.  Sources properties from
   * `/api/properties` (no PILOT_STORE_PROPERTIES merge, no
   * localStorage mirror).
   *
   * iter-361 · F-05 · Command Centre cold-load performance — the
   * two requests are genuinely independent (no field on `deals`
   * depends on the just-fetched `properties` list, and vice-versa
   * — each hydrator has its own setter path).  Fire them in
   * parallel so the KPI strip can compute Expected Banking as
   * soon as `/api/deals` returns, without waiting on the
   * separate `/api/properties` round-trip.  Setters are still
   * called from within their own promise chain so React batching
   * and error handling are unchanged. */
  const refreshWorkspace = useCallback(async () => {
    if (!user) return;
    setPropertiesLoading(true);
    const propertiesTask = api.get("/api/properties")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : [];
        setProperties(rows.map(propertyFromApi));
      })
      .catch(() => { setProperties([]); })
      .finally(() => { setPropertiesLoading(false); });
    // hydrateDeals owns its own loading + error state, so we just
    // await it in parallel.  Promise.all waits for the slower of
    // the two before returning — the setters have already fired
    // whichever way, so any effect keyed on `deals` / `properties`
    // wakes up as soon as its input resolves.
    await Promise.all([propertiesTask, hydrateDeals()]);
  }, [user, hydrateDeals]);

  /* iter-alignment · P54 — Retired.
   *
   * The previous "pilot overlay safety-net" effect + user-row
   * persistence effects mirrored deals + properties into
   * localStorage on every state change so the pilot dataset could
   * never be wiped by a stray setter.  With the live API SSOT
   * (`/api/deals`, `/api/properties`) the API IS the truth — every
   * write flows back through the transforms and hydrate cycle, so
   * there is nothing to mirror or overlay.  The `applyPilotOverrides`
   * layer inside `hydrateDeals` still re-applies the narrow set of
   * client-only side-effects documented above. */

  useEffect(() => {
    if (!user) {
      setProperties([]); setDeals([]);
      setPropertiesLoading(false); setDealsLoading(false); setDealsHydrated(false);
      return;
    }
    // Persist current org_id so subsequent renders can detect drift
    // (mobile Safari Zustand-style stale-cache symptom).
    try { localStorage.setItem("chainflow:lastOrgId", user.org_id || ""); }
    catch { /* private mode — fine */ }

    let cancelled = false;
    refreshWorkspace().then(() => {
      if (cancelled) return;
    });
    return () => { cancelled = true; };
    // CRITICAL: include user.org_id so workspace switches refetch.
    // Previously this only depended on user.id which missed the
    // multi-office org-switch case observed in production.
  }, [user?.id, user?.org_id, refreshWorkspace]);

  /* Cross-surface refetch triggers — workspace seed / clear sample data
     fire on the global window bus. Any time those land we hydrate
     fresh so stage PATCHes targeting newly-replaced IDs stop 404-ing.
     Also re-hydrate when the tab returns to the foreground (mobile
     Safari and iPad PWA both park JS for minutes — the deal IDs in
     memory can be stale by the time the operator unlocks). */
  useEffect(() => {
    if (!user) return;
    const onSeedDone = () => { hydrateDeals(); };
    const onClearDone = () => { hydrateDeals(); };
    // Milestone writes can move canonical stage/status (exchange, completion) — refetch that deal.
    const onDealRefresh = async (e) => {
      const { dealId, reason } = e?.detail || {};
      if (!dealId || !String(reason || "").startsWith("milestone.")) return;
      try {
        const { data: fresh } = await api.get(`/api/v2/deals/${dealId}`);
        const merged = dealFromApi(fresh);
        setDeals((arr) => arr.map((x) => (x.id === dealId ? { ...x, ...merged } : x)));
      } catch (_) { /* next natural refresh will catch it */ }
    };
    const onForeground = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        hydrateDeals();
      }
    };
    window.addEventListener("cf:workspace-seeded", onSeedDone);
    window.addEventListener("cf:workspace-cleared", onClearDone);
    window.addEventListener("cf:deal:refresh", onDealRefresh);
    document.addEventListener("visibilitychange", onForeground);
    return () => {
      window.removeEventListener("cf:workspace-seeded", onSeedDone);
      window.removeEventListener("cf:workspace-cleared", onClearDone);
      window.removeEventListener("cf:deal:refresh", onDealRefresh);
      document.removeEventListener("visibilitychange", onForeground);
    };
  }, [user, hydrateDeals]);

  const addProperty = useCallback(async (input) => {
    // iter-alignment · P54 — Live API SSOT.  Dedupe by
    // address+postcode against the in-memory state (which is
    // sourced from `/api/properties`); the API layer is the
    // single truth for what already exists.
    const incomingAddr = String(input?.address || "").trim().toLowerCase();
    const incomingPostcode = String(input?.postcode || "").trim().toUpperCase().replace(/\s+/g, " ");
    const existing = properties.find((p) => {
      const a = String(p.address || "").trim().toLowerCase();
      const pc = String(p.postcode || "").trim().toUpperCase().replace(/\s+/g, " ");
      return incomingAddr && a === incomingAddr && (!incomingPostcode || pc === incomingPostcode);
    });
    if (existing) {
      recentRef.current.properties = [existing.id, ...recentRef.current.properties.filter((x) => x !== existing.id)].slice(0, 5);
      return existing;
    }
    const { data } = await api.post("/api/properties", propertyToApi(input || {}));
    const p = propertyFromApi(data);
    setProperties((arr) => [p, ...arr.filter((x) => x.id !== p.id)]);
    recentRef.current.properties = [p.id, ...recentRef.current.properties.filter((x) => x !== p.id)].slice(0, 5);
    return p;
  }, [properties]);

  const updateProperty = useCallback(async (pid, patch) => {
    /* iter-alignment · P0 · Property Edit — Save Failure Trace
     * (2026-02-28) — Add a dev-only breadcrumb around the PATCH
     * so a `That property could not be found in your workspace.`
     * 404 surfaces the exact ID we sent + HTTP status in the
     * browser console.  In production the log is suppressed so
     * we don't leak workspace identifiers. */
    const _dev = typeof process !== "undefined" && process?.env?.NODE_ENV !== "production";
    try {
      const { data } = await api.patch(`/api/properties/${pid}`, propertyToApi(patch || {}));
      const p = propertyFromApi(data);
      setProperties((arr) => arr.map((x) => (x.id === pid ? p : x)));
      /* iter-alignment · P0 · Property Address Edit Reset Regression
       * (2026-02-28)
       * -----------------------------------------------------------
       * Property PATCH is a self-contained mutation for MOST fields
       * — the backend only writes to `db.properties`, never fans
       * out to deals.  Previously we called `hydrateDeals()` as a
       * "fire and forget dependent refresh" which clobbered every
       * optimistic patch `patchDealLocally` had applied.
       *
       * EXCEPTION — iter-alignment · P0 · BSA Property Flag →
       * Transaction Workflow (2026-02-28):
       * `Property.bsa_applies` IS fanned onto every linked
       * `Deal.applicable_bsa` (`routes_deals::_enrich_deals_with_bsa`).
       * When that flag flips, downstream consumers (journey rail,
       * milestone list, readiness / risk) MUST recompute
       * immediately or the visible timeline drifts from the
       * SSOT until the next manual refresh.  Rather than a
       * blanket `hydrateDeals()` (which would still clobber
       * unrelated optimistic patches), we do a SURGICAL refetch
       * of just the deals linked to this property via
       * `GET /api/deals/{id}` and merge each response through
       * the existing `patchDealLocally`.  Optimistic state on
       * other deals is untouched; the linked deals' derived
       * `applicable_bsa` + `applicableBSA` fields are refreshed
       * so the journey resolver + milestone predicate fire
       * with the new value.
       */
      if (patch && Object.prototype.hasOwnProperty.call(patch, "bsaApplies")) {
        const linkedIds = Array.from(new Set(
          (deals || [])
            .filter((d) => d && (d.propertyId || d.property_id) === pid)
            .map((d) => d.id)
        ));
        if (linkedIds.length > 0) {
          /* iter-alignment · BSA Journey Delayed Refresh
           *   (2026-09 · latency fix) — Optimistically patch
           *   `applicableBSA` on each linked deal so
           *   `optsForDeal` (transactionJourney.js) sees the
           *   new applicability on the SAME tick as the
           *   dispatched event.  The rail therefore flips at
           *   ~2s (right after PATCH) instead of waiting on
           *   the slow (~5s) fan-out GET below.  Fan-out
           *   still runs as a fire-and-forget reconciliation
           *   so any server-side derivations refresh, but it
           *   does not gate the visible refresh. */
          const nextBsa = !!patch.bsaApplies;
          setDeals((arr) => arr.map((x) => (
            linkedIds.includes(x.id)
              ? { ...x, applicableBSA: nextBsa, applicable_bsa: nextBsa }
              : x
          )));
          if (typeof window !== "undefined") {
            linkedIds.forEach((dealId) => {
              try {
                window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
                  detail: { dealId, reason: "property.bsa_applies" },
                }));
              } catch (_) { /* noop */ }
            });
          }
          // Fire-and-forget deal fan-out — reconciles the full
          // deal blob in the background without gating the
          // caller (drawer submit, overflow onSaved reload,
          // toast, or the rail update above).
          void Promise.all(linkedIds.map(async (dealId) => {
            try {
              const { data: fresh } = await api.get(`/api/v2/deals/${dealId}`);
              const merged = dealFromApi(fresh);
              setDeals((arr) => arr.map((x) => (x.id === dealId ? { ...x, ...merged } : x)));
            } catch (_) { /* next natural refresh will catch it */ }
          }));
        }
      }
      return p;
    } catch (err) {
      if (_dev) {
        // eslint-disable-next-line no-console
        console.error("[updateProperty] PATCH failed", {
          requestedId: pid,
          status: err?.response?.status,
          detail: err?.response?.data,
        });
      }
      throw err;
    }
  }, [deals]);

  const deleteProperty = useCallback(async (pid) => {
    await api.delete(`/api/properties/${pid}`);
    setProperties((arr) => arr.filter((p) => p.id !== pid));
  }, []);

  // ─── Deals (server-backed) ────────────────────────────────────────────────
  // POST /api/deals returns { deal, summary } where summary is the kickoff
  // automation report (tasks/milestones/memo/risk). We attach it onto the
  // returned object as `__kickoff` so the create-deal modal can hand it to
  // <DealKickoffCelebration> for the animated reveal.
  const addDeal = useCallback(async (input) => {
    const { data } = await api.post("/api/deals", dealToApi(input || {}));
    const dealPayload = data?.deal || data;
    const d = dealFromApi(dealPayload);
    if (data?.summary) d.__kickoff = data.summary;
    // iter-alignment · P54 — Live API SSOT.  The API returned row IS
    // the truth; no localStorage mirror is needed.  The next
    // `useNavigate("/deal-rooms/:id")` will find the deal because
    // we set it into state synchronously here.
    setDeals((arr) => [d, ...arr.filter((x) => x.id !== d.id)]);
    // Kickoff fires server-side, which seeds tasks + a notification —
    // re-hydrate them so the panels light up live.
    try {
      const [t, n] = await Promise.all([
        api.get("/api/tasks"),
        api.get("/api/notifications"),
      ]);
      setTasks((Array.isArray(t.data) ? t.data : []).map(taskFromApi));
      setNotifications((Array.isArray(n.data) ? n.data : []).map(notifFromApi));
    } catch (_e) { /* noop */ }
    // Reflect server-side cascade: refresh property if its status changed.
    if (d.propertyId) {
      try {
        const { data: p } = await api.get(`/api/properties/${d.propertyId}`);
        const fresh = propertyFromApi(p);
        setProperties((arr) => arr.map((x) => (x.id === fresh.id ? fresh : x)));
      } catch (_e) { /* noop */ }
    }
    return d;
  }, []);

  const updateDeal = useCallback(async (dealId, patch) => {
    const current = deals.find((x) => x.id === dealId);
    const resolvedPatch = typeof patch === "function" ? patch(current || {}) : (patch || {});
    // v2-only deals (Postgres-native, no Mongo row) write to /api/v2.
    if (current?._v2only) {
      const v2body = {};
      if (resolvedPatch.stage  !== undefined) v2body.stage  = resolvedPatch.stage;
      if (resolvedPatch.status !== undefined) v2body.status = resolvedPatch.status;
      if (resolvedPatch.name   !== undefined) v2body.name   = resolvedPatch.name;
      if (resolvedPatch.dealHealthScore     !== undefined) v2body.deal_health_score    = resolvedPatch.dealHealthScore;
      if (resolvedPatch.exchangeProbability !== undefined) v2body.exchange_probability = resolvedPatch.exchangeProbability;
      if (resolvedPatch.targetExchange      !== undefined) v2body.target_exchange      = resolvedPatch.targetExchange;
      if (resolvedPatch.targetCompletion    !== undefined) v2body.target_completion    = resolvedPatch.targetCompletion;
      if (resolvedPatch.saleAgreedDate      !== undefined) v2body.sale_agreed_date     = resolvedPatch.saleAgreedDate;
      /* iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
       * (2026-03-05) — thread `is_cash_buyer` and `funding_basis`
       * onto the v2 PATCH body so Quick Edit "Purchase method"
       * (and any other consumer that writes these flags via the
       * generic PATCH) persists to Postgres.  The backend's
       * bidirectional sync (see `routes_persistence.update_deal`)
       * keeps the paired column coherent, so writing either alone
       * is sufficient. */
      if (resolvedPatch.is_cash_buyer       !== undefined) v2body.is_cash_buyer        = resolvedPatch.is_cash_buyer;
      if (resolvedPatch.isCashBuyer         !== undefined) v2body.is_cash_buyer        = resolvedPatch.isCashBuyer;
      if (resolvedPatch.funding_basis       !== undefined) v2body.funding_basis        = resolvedPatch.funding_basis;
      if (resolvedPatch.fundingBasis        !== undefined) v2body.funding_basis        = resolvedPatch.fundingBasis;
      const { data } = await api.patch(`/api/v2/deals/${dealId}`, v2body);
      const merged = normaliseDealDates({ ...current,
        stage: data.stage, status: data.status, risk: data.risk,
        agreedPrice: data.agreed_price,
        dealHealthScore: data.deal_health_score,
        exchangeProbability: data.exchange_probability,
        targetExchange: data.target_exchange,
        targetCompletion: data.target_completion,
        saleAgreedDate: data.sale_agreed_date ?? null,
        sale_agreed_date: data.sale_agreed_date ?? null,
        /* iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
         * (2026-03-05) — merge the canonical funding_basis +
         * is_cash_buyer off the response so the store deal is
         * always consistent with what the SSOT persisted (in
         * particular the backend's bidirectional sync may have
         * flipped the paired column). */
        funding_basis: data.funding_basis,
        fundingBasis:  data.funding_basis,
        is_cash_buyer: data.is_cash_buyer,
        isCashBuyer:   data.is_cash_buyer,
      });
      setDeals((arr) => arr.map((x) => (x.id === dealId ? merged : x)));
      return merged;
    }
    const body = dealToApi(resolvedPatch);
    const { data } = await api.patch(`/api/deals/${dealId}`, body);
    const d = dealFromApi(data);
    setDeals((arr) => arr.map((x) => (x.id === dealId ? d : x)));
    return d;
  }, [deals]);

  /* Optimistic-only setter — patches the in-memory deal without hitting
     the API. Used by surfaces that want to feel instant (e.g. stage
     progression rail) while the real network write happens in the
     background. Caller is responsible for reverting on failure by
     calling this helper again with the previous patch. */
  const patchDealLocally = useCallback((dealId, patch) => {
    setDeals((arr) => arr.map((x) => (x.id === dealId ? { ...x, ...patch } : x)));
  }, []);

  /* iter-318 — Inline milestone editing. Persists a per-stage edit
   * (status / owner / target date / note / completedAt) to the
   * pilot-overrides layer so the Deal Rooms timeline + Context
   * column reflect the change across refreshes.
   *
   * `milestonePatch` is a partial — only the supplied keys
   * overwrite. Returns the freshly-merged milestoneStates map so
   * the caller can render an optimistic result before the React
   * state propagates.
   */
  const patchMilestoneState = useCallback((dealId, stageId, milestonePatch) => {
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    const prevStates = (prev.milestoneStates && typeof prev.milestoneStates === "object")
      ? prev.milestoneStates
      : {};
    const prevForStage = prevStates[stageId] || {};
    const nextForStage = { ...prevForStage, ...milestonePatch, updatedAt: new Date().toISOString() };
    const nextStates = { ...prevStates, [stageId]: nextForStage };
    overrides[dealId] = { ...prev, milestoneStates: nextStates };
    savePilotOverrides(overrides);
    setDeals((arr) => arr.map((x) =>
      x.id === dealId ? { ...x, milestoneStates: nextStates, _pilotOverridden: true } : x,
    ));
    return nextStates;
  }, []);

  /* logOperationalEvent — single canonical helper for the
     "What happened?" interaction. Every surface that needs to log
     an operational event MUST route through here so:

       1) Tenancy self-heal is consistent — on 404 we hydrate the
          workspace then retry exactly once before surfacing a
          calm error toast.
       2) Optimistic + revert UI is uniform — stage advances are
          applied locally before the network call; reverted on
          any failure.
       3) Structured client-side logs are emitted on every call so
          production observability can trace which surface fired
          which event for which deal in which tenancy state.

     Returns the backend response on success; throws on
     unrecoverable failure (surface decides toast copy).

     `opts`:
       - surface: short label for the originating UI (e.g.
         "deal-room-composer", "progression-row", "what-happened-sheet").
       - optimisticAdvanceTo: when supplied, the local store moves
         the deal's stage immediately and reverts on failure.
       - notes: free-text operator note attached to the event row.
  */
  const logOperationalEvent = useCallback(async (dealId, eventKey, opts = {}) => {
    const surface = opts.surface || "unknown";
    const current = deals.find((x) => x.id === dealId);
    const prevStage = current?.stage;
    const v2only = !!current?._v2only;

    // Optimistic local update — best-effort, reverted on failure.
    let applied = false;
    if (opts.optimisticAdvanceTo && prevStage && opts.optimisticAdvanceTo !== prevStage) {
      try {
        setDeals((arr) => arr.map((x) =>
          x.id === dealId ? { ...x, stage: opts.optimisticAdvanceTo } : x,
        ));
        applied = true;
      } catch { /* noop */ }
    }

    const post = async () => api.post(`/api/deals/${dealId}/operational-event`, {
      event_key: eventKey,
      notes: opts.notes || null,
    });

    let retried = false;
    try {
      // eslint-disable-next-line no-console
      console.info("[chainflow] op-event.attempt", {
        surface, dealId, eventKey, v2only,
      });
      let resp;
      try {
        resp = await post();
      } catch (firstErr) {
        const fStatus = firstErr?.response?.status;
        const fCode   = firstErr?.response?.data?.detail?.code;
        if (fStatus === 404 && fCode === "deal_not_found") {
          retried = true;
          // eslint-disable-next-line no-console
          console.info("[chainflow] op-event.404 — hydrating + retrying", {
            surface, dealId, eventKey, v2only,
          });
          // hydrateDeals is defined above in this module — at this
          // point in the closure it's already declared.
          if (typeof hydrateDeals === "function") {
            await hydrateDeals();
          }
          resp = await post();
        } else {
          throw firstErr;
        }
      }
      const data = resp?.data;
      // Reconcile: if the backend says stage didn't advance but we
      // optimistically moved it, revert.
      if (applied && data && data.stage_advanced === false) {
        try {
          setDeals((arr) => arr.map((x) =>
            x.id === dealId ? { ...x, stage: prevStage } : x,
          ));
        } catch { /* noop */ }
      } else if (data?.stage && data.stage !== prevStage) {
        try {
          setDeals((arr) => arr.map((x) =>
            x.id === dealId ? { ...x, stage: data.stage } : x,
          ));
        } catch { /* noop */ }
      }
      // eslint-disable-next-line no-console
      console.info("[chainflow] op-event.ok", {
        surface, dealId, eventKey, v2only, retried,
        idempotent: !!data?.idempotent, stage_advanced: !!data?.stage_advanced,
      });
      return data;
    } catch (err) {
      // Always revert optimistic before bubbling.
      if (applied) {
        try {
          setDeals((arr) => arr.map((x) =>
            x.id === dealId ? { ...x, stage: prevStage } : x,
          ));
        } catch { /* noop */ }
      }
      // eslint-disable-next-line no-console
      console.warn("[chainflow] op-event.failed", {
        surface, dealId, eventKey, v2only, retried,
        code: err?.response?.data?.detail?.code,
        status: err?.response?.status,
      });
      throw err;
    }
  }, [deals, hydrateDeals]);

  const deleteDeal = useCallback(async (dealId) => {
    await api.delete(`/api/deals/${dealId}`);
    setDeals((arr) => arr.filter((d) => d.id !== dealId));
  }, []);

  const archiveDeal = useCallback(async (dealId) => {
    const { data } = await api.post(`/api/deals/${dealId}/archive`);
    const d = dealFromApi(data);
    setDeals((arr) => arr.map((x) => (x.id === dealId ? d : x)));
    return d;
  }, []);

  const restoreDeal = useCallback(async (dealId) => {
    const { data } = await api.post(`/api/deals/${dealId}/restore`);
    const d = dealFromApi(data);
    setDeals((arr) => arr.map((x) => (x.id === dealId ? d : x)));
    return d;
  }, []);

  const advanceStage = useCallback(async (dealId, toStageId, opts = {}) => {
    /* iter-alignment · P — Move to next stage (2026-08-17).
     * `opts.manual` + `opts.outstandingCount` propagate through
     * to the backend so the audit trail captures the operator
     * override AND the count of milestones that remained
     * outstanding at the moment of advance.  No milestones are
     * completed by this call — the checklist stays editable. */
    const manual = Boolean(opts && opts.manual);
    const outstandingCount =
      typeof opts?.outstandingCount === "number" && opts.outstandingCount >= 0
        ? Math.floor(opts.outstandingCount)
        : null;
    const current = deals.find((x) => x.id === dealId);
    // iter-270F — Pilot deals are frontend-only by design. Persist
    // the stage advancement via the pilot-override layer so refresh,
    // Forecasting Centre and Deal Rooms see the new stage. No backend
    // call (the deal doesn't exist in the DB).
    if (current?._pilot) {
      const overrides = loadPilotOverrides();
      const prev = overrides[dealId] || {};
      overrides[dealId] = { ...prev, stage: toStageId };
      savePilotOverrides(overrides);
      const merged = { ...current, stage: toStageId, _pilotOverridden: true };
      setDeals((arr) => arr.map((x) => (x.id === dealId ? merged : x)));
      return merged;
    }
    // v2-only deals don't have a Mongo /advance endpoint — patch the stage
    // directly on /api/v2 (also auto-logs a stage_change activity row).
    if (current?._v2only) {
      const { data } = await api.patch(`/api/v2/deals/${dealId}`, { stage: toStageId });
      const merged = { ...current,
        stage: data.stage, status: data.status, risk: data.risk,
        dealHealthScore: data.deal_health_score,
        exchangeProbability: data.exchange_probability,
      };
      setDeals((arr) => arr.map((x) => (x.id === dealId ? merged : x)));
      return merged;
    }
    const body = { to: toStageId };
    if (manual) {
      body.manual = true;
      if (outstandingCount !== null) body.outstanding_count = outstandingCount;
    }
    const { data } = await api.post(`/api/deals/${dealId}/advance`, body);
    const d = dealFromApi(data);
    setDeals((arr) => arr.map((x) => (x.id === dealId ? d : x)));
    // Reflect property cascade for exchanged/completed.
    if (d.propertyId && (toStageId === "exchanged" || toStageId === "completed")) {
      try {
        const { data: p } = await api.get(`/api/properties/${d.propertyId}`);
        const fresh = propertyFromApi(p);
        setProperties((arr) => arr.map((x) => (x.id === fresh.id ? fresh : x)));
      } catch (_e) { /* noop */ }
    }
    return d;
  }, [deals]);

  /* iter-270F — Pilot Mark Complete handler.
   *
   * Pilot deals (the 18-property canonical demo) don't exist in the
   * backend database — they are frontend-only canonical content. The
   * journey-step complete endpoint validates `deal_id` as a UUID
   * AND requires the deal to exist; for pilot deals neither holds.
   *
   * This helper:
   *   1. Records the completed journey step against the deal
   *   2. Advances the deal's stage (per JOURNEY_STEP_META.advances_to)
   *   3. Appends a calm activity row
   *   4. Persists everything to localStorage so refresh / Forecasting /
   *      Deal Rooms see the advancement.
   *
   * Callers MUST pre-check `deal._pilot === true` before invoking
   * this helper — real deals still go through the canonical
   * /api/v2/.../complete endpoint. */
  const markPilotJourneyStepComplete = useCallback((dealId, stepId, opts = {}) => {
    const STEP_ADVANCES_TO = {
      offer:               "aml",
      aml:                 "memo",
      draft_contract:      "solicitors",
      mgmt_pack_requested: "solicitors",
      mgmt_pack_received:  "searches",
      searches:            "enquiries",
      mortgage_offer:      "mortgage_offer",
      leasehold_enquiries: "enquiries",
      contracts:           "exchange_ready",
      exchange:            "exchanged",
      completion:          "completed",
    };
    const STEP_LABELS = {
      offer:               "Offer Agreed",
      aml:                 "AML Checks",
      draft_contract:      "Draft Contract",
      mgmt_pack_requested: "Management Pack Requested",
      mgmt_pack_received:  "Management Pack Received",
      searches:            "Searches",
      mortgage_offer:      "Mortgage Offer",
      leasehold_enquiries: "Leasehold Enquiries",
      contracts:           "Contracts",
      exchange:            "Exchange",
      completion:          "Completion",
    };
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    const completedSet = new Set(prev.completedSteps || []);
    completedSet.add(stepId);
    let stage = opts.stage || STEP_ADVANCES_TO[stepId] || prev.stage;
    const label = STEP_LABELS[stepId] || stepId.replace(/_/g, " ");

    /* iter-374/376 — Pilot mirror of the backend cascade. When the
     * trigger step is one of the pre-journey nodes (offer / memo /
     * aml) fold the remaining pre-journey steps into completedSet
     * AND advance the stage directly to the first canonical rail
     * milestone (`mortgage_app` for chain / mortgage buyers,
     * `solicitors` for cash buyers) so the pilot experience
     * matches the real API path. Every cascaded step still gets a
     * milestone activity row so directors see the same audit
     * trail on both surfaces. */
    const PRE_JOURNEY_ORDER = ["offer", "memo", "aml"];
    const cascadedActivity = [];
    if (PRE_JOURNEY_ORDER.includes(stepId)) {
      // Look up the deal to decide chain vs cash.
      const dealRecord = (deals || []).find((x) => x.id === dealId) || {};
      const isCash = !!(dealRecord.is_cash_buyer || dealRecord.isCashBuyer || dealRecord.financeType === "cash");
      for (const cascadeStep of PRE_JOURNEY_ORDER) {
        if (cascadeStep === stepId) continue;
        if (completedSet.has(cascadeStep)) continue;
        completedSet.add(cascadeStep);
        const cascadeLabel = STEP_LABELS[cascadeStep] || cascadeStep;
        const cascadeCopy = cascadeStep === "memo"
          ? "Memorandum of Sale issued · auto-generated when the offer was agreed."
          : cascadeStep === "aml"
            ? "AML checks queued · compliance workflow triggered automatically."
            : `${cascadeLabel} recorded automatically as the transaction started.`;
        cascadedActivity.push({
          id: `pilot-${dealId}-${cascadeStep}-${Date.now()}-cascade`,
          type: "milestone",
          kind: "milestone_completed",
          label: cascadeCopy,
          title: cascadeCopy,
          actor_role: "System",
          auto: true,
          cascade_from: stepId,
          created_at: new Date().toISOString(),
        });
      }
      stage = isCash ? "solicitors" : "mortgage_app";
      cascadedActivity.push({
        id: `pilot-${dealId}-${stepId}-${Date.now()}-transaction-started`,
        type: "milestone",
        kind: "stage_change",
        label: `Transaction started · ${stepId} → ${stage}`,
        title: `Transaction started · ${stepId} → ${stage}`,
        actor_role: "System",
        auto: true,
        cascade_from: stepId,
        created_at: new Date().toISOString(),
      });
    }

    const activityRow = {
      id: `pilot-${dealId}-${stepId}-${Date.now()}`,
      type: "milestone",
      kind: "milestone_completed",
      label: `${label} marked complete`,
      title: `${label} marked complete`,
      actor_role: "Estate Agent",
      created_at: new Date().toISOString(),
    };
    overrides[dealId] = {
      ...prev,
      stage,
      completedSteps: [...completedSet],
      activity: [activityRow, ...cascadedActivity, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    // Reflect immediately in chainStore state so the UI updates.
    setDeals((arr) =>
      arr.map((x) => {
        if (x.id !== dealId) return x;
        // iter-276 — completedSteps must propagate to live state so
        // resolveJourneyProgress() in DealRoomCanonicalPage sees
        // the step as DONE on the very next render (no API
        // refresh required for pilot deals). The set is unioned
        // with any existing completedSteps so this is safely
        // idempotent and doesn't lose prior completions.
        const prevSteps = new Set(Array.isArray(x.completedSteps) ? x.completedSteps : []);
        prevSteps.add(stepId);
        // iter-374/376 — Also fold cascaded pre-journey completions
        // into the live state so the deal room re-derives the
        // same rail on the very next render.
        for (const cs of completedSet) prevSteps.add(cs);
        return {
          ...x,
          stage,
          completedSteps: [...prevSteps],
          activity: [activityRow, ...cascadedActivity, ...((x.activity) || [])],
          _pilotOverridden: true,
        };
      })
    );
    return { dealId, stepId, newStage: stage, activity: activityRow, cascadedActivity };
  }, [deals]);

  /* iter-297 — Pilot document upload.
   *
   * Pilot deals are seeded frontend-only (chainStore), so the
   * canonical /api/deals/:id/documents POST returns 404 and the
   * deal-access endpoint flips to read-only. We give pilot deals
   * a local upload path so the Upload Document button + drag-drop
   * area in the canonical Deal Room work end-to-end without a
   * backend round trip: the file metadata lands in the deal's
   * documents list + an activity row, both persisted via the
   * pilot-override layer so the upload survives refresh.
   *
   * Callers MUST pre-check `deal._pilot === true` before invoking;
   * real deals still go through /api/deals/:id/documents. */
  // iter-302 — Pilot activity logger. Used by the Quick Edit Deal
  // side-sheet (and any future pilot-only write path) to append a
  // calm audit row to a seeded deal without hitting the backend.
  // Persisted via the same pilot-override layer documents/uploads
  // already use, so edits survive refresh.
  const appendPilotActivity = useCallback((dealId, activity) => {
    if (!dealId || !activity) return null;
    const createdAt = activity.created_at || new Date().toISOString();
    const row = {
      id: activity.id || `pilot-act-${dealId}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      type: activity.type || "deal_edit",
      kind: activity.type || "deal_edit",
      activity_type: activity.type || "deal_edit",
      title: activity.title || activity.label || "Deal updated",
      label: activity.label || activity.title || "Deal updated",
      description: activity.description || "",
      actor_role: activity.actor_role || "Estate Agent",
      created_at: createdAt,
    };
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    overrides[dealId] = {
      ...prev,
      activity: [row, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    setDeals((arr) =>
      arr.map((x) =>
        x.id !== dealId
          ? x
          : { ...x, activity: [row, ...(x.activity || [])], _pilotOverridden: true }
      )
    );
    return row;
  }, []);

  const appendPilotDocument = useCallback((dealId, file) => {
    if (!dealId || !file) return null;
    const docId = `pilot-doc-${dealId}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const createdAt = new Date().toISOString();
    const doc = {
      id: docId,
      deal_id: dealId,
      name: file.name || "Untitled document",
      doc_type: "uploaded",
      size: typeof file.size === "number" ? file.size : null,
      mime: file.type || null,
      uploaded_by: "Estate Agent",
      created_at: createdAt,
      uploaded_at: createdAt,
      _pilot: true,
    };
    const activityRow = {
      id: `pilot-act-${docId}`,
      type: "document_upload",
      kind: "document_upload",
      activity_type: "document_upload",
      title: `${doc.name} uploaded`,
      label: `${doc.name} uploaded`,
      description: "Uploaded via the canonical Deal Room dropzone",
      actor_role: "Estate Agent",
      created_at: createdAt,
    };
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    overrides[dealId] = {
      ...prev,
      documents: [doc, ...(prev.documents || [])].slice(0, 200),
      activity:  [activityRow, ...(prev.activity  || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    setDeals((arr) =>
      arr.map((x) => {
        if (x.id !== dealId) return x;
        return {
          ...x,
          documents: [doc, ...(x.documents || [])],
          activity:  [activityRow, ...(x.activity  || [])],
          _pilotOverridden: true,
        };
      })
    );
    // iter-336/Δ — Fire the canonical Documents → Timeline
    // cascade so an `appendPilotDocument` upload still advances
    // the matching milestone (Contract Pack → Contract,
    // Mortgage Offer → Mortgage, etc) without going through the
    // backend POST path. Routed via `_automateDocUploadRef` so
    // the cascade reads the LATEST `deals` state.
    try { _automateDocUploadRef.current?.(dealId, doc); } catch (_e) { /* noop */ }
    return doc;
  }, []);

  /* iter-336 — Pilot document REMOVE.
   *
   * Removes a document from the pilot override layer + live deal
   * state, and logs a calm `document_removed` activity row so the
   * timeline keeps a complete audit trail. Persists via the same
   * localStorage override layer used by uploads so the deletion
   * survives refresh. Real (API-backed) documents go through the
   * canonical DELETE endpoint and don't hit this helper. */
  const removePilotDocument = useCallback((dealId, docId) => {
    if (!dealId || !docId) return false;
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    const prevDocs = Array.isArray(prev.documents) ? prev.documents : [];
    const target = prevDocs.find((d) => d?.id === docId);
    const nextDocs = prevDocs.filter((d) => d?.id !== docId);
    const createdAt = new Date().toISOString();
    const activityRow = {
      id: `pilot-act-rm-${docId}-${Date.now()}`,
      type: "document_removed",
      kind: "document_removed",
      activity_type: "document_removed",
      title: `${target?.name || "Document"} removed`,
      label: `${target?.name || "Document"} removed`,
      description: "Removed from the Document Centre",
      actor_role: "Estate Agent",
      created_at: createdAt,
    };
    // iter-336/Δ — Tombstone the docId so seeded pilot documents
    // (which live inside `pilotData.js` and would otherwise rehydrate
    // on every page reload) stay deleted. Real upload-via-helper docs
    // already drop out of `prev.documents` above, but tombstoning
    // them here is harmless and keeps the merge in `applyPilotOverrides`
    // perfectly deterministic.
    const prevRemoved = Array.isArray(prev.removedDocIds) ? prev.removedDocIds : [];
    const nextRemoved = prevRemoved.includes(docId) ? prevRemoved : [...prevRemoved, docId];
    overrides[dealId] = {
      ...prev,
      documents: nextDocs,
      removedDocIds: nextRemoved,
      activity: [activityRow, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    setDeals((arr) =>
      arr.map((x) => {
        if (x.id !== dealId) return x;
        return {
          ...x,
          documents: (x.documents || []).filter((d) => d?.id !== docId),
          activity: [activityRow, ...(x.activity || [])],
          _pilotOverridden: true,
        };
      })
    );
    return true;
  }, []);

  /* iter-336 — Pilot document REPLACE.
   *
   * Atomically removes the old pilot document and appends a new one
   * from `file`, mirroring the canonical "Replace" surface. A single
   * `document_replaced` activity row is logged in place of separate
   * upload + remove events so the timeline stays calm. */
  const replacePilotDocument = useCallback((dealId, docId, file) => {
    if (!dealId || !docId || !file) return null;
    const newDocId = `pilot-doc-${dealId}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const createdAt = new Date().toISOString();
    const newDoc = {
      id: newDocId,
      deal_id: dealId,
      name: file.name || "Untitled document",
      doc_type: "uploaded",
      size: typeof file.size === "number" ? file.size : null,
      mime: file.type || null,
      uploaded_by: "Estate Agent",
      created_at: createdAt,
      uploaded_at: createdAt,
      _pilot: true,
    };
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    const prevDocs = Array.isArray(prev.documents) ? prev.documents : [];
    const target = prevDocs.find((d) => d?.id === docId);
    // Preserve the original doc's tag/template_id so the timeline
    // automation continues to fire against the same canonical row.
    if (target?.template_id && !newDoc.template_id) newDoc.template_id = target.template_id;
    if (target?.tag && !newDoc.tag) newDoc.tag = target.tag;
    const activityRow = {
      id: `pilot-act-rep-${newDocId}`,
      type: "document_replaced",
      kind: "document_replaced",
      activity_type: "document_replaced",
      title: `${newDoc.name} replaces ${target?.name || "previous version"}`,
      label: `${newDoc.name} replaces ${target?.name || "previous version"}`,
      description: "Replaced via the Document Centre",
      actor_role: "Estate Agent",
      created_at: createdAt,
    };
    const nextDocs = [newDoc, ...prevDocs.filter((d) => d?.id !== docId)].slice(0, 200);
    // iter-336/Δ — Tombstone the OLD docId so the seeded pilot
    // version (if any) doesn't rehydrate on the next page reload.
    const prevRemoved = Array.isArray(prev.removedDocIds) ? prev.removedDocIds : [];
    const nextRemoved = prevRemoved.includes(docId) ? prevRemoved : [...prevRemoved, docId];
    overrides[dealId] = {
      ...prev,
      documents: nextDocs,
      removedDocIds: nextRemoved,
      activity: [activityRow, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    setDeals((arr) =>
      arr.map((x) => {
        if (x.id !== dealId) return x;
        return {
          ...x,
          documents: [newDoc, ...((x.documents || []).filter((d) => d?.id !== docId))],
          activity: [activityRow, ...(x.activity || [])],
          _pilotOverridden: true,
        };
      })
    );
    // iter-336/Δ — A Replace IS conceptually a fresh upload of
    // the same canonical doc — fire the Documents → Timeline
    // cascade so the matching milestone advances if the new
    // filename maps onto a recognised template. Routed via
    // `_automateDocUploadRef` so the cascade reads the LATEST
    // `deals` state instead of the stale first-render closure.
    try { _automateDocUploadRef.current?.(dealId, newDoc); } catch (_e) { /* noop */ }
    return newDoc;
  }, []);

  // iter-270I — Pilot document request lifecycle.
  // Creates a pending request row + appends an activity event to the
  // pilot deal so the Documents Hub (Pending Requests) and the deal's
  // timeline both reflect the new request immediately.
  const createPilotDocumentRequest = useCallback((dealId, payload) => {
    const reqId = `pilot-docreq-${dealId}-${Date.now()}`;
    const createdAt = new Date().toISOString();
    const row = {
      id: reqId,
      deal_id: dealId,
      template_id: payload?.template_id || "other",
      title: payload?.title || "Document requested",
      doc_label: payload?.doc_label || payload?.label || "Document",
      requested_from_role: payload?.requested_from_role || null,
      requested_from_name: payload?.requested_from_name || null,
      requested_from_id:   payload?.requested_from_id   || null,
      note: payload?.note || null,
      status: "pending",
      created_at: createdAt,
      fulfilled_at: null,
      fulfilled_doc_id: null,
    };
    const all = loadPilotDocRequests();
    const list = Array.isArray(all[dealId]) ? all[dealId] : [];
    all[dealId] = [row, ...list];
    savePilotDocRequests(all);
    // Mirror an activity row so the Deal Room timeline updates instantly.
    const activityRow = {
      id: `pilot-act-${reqId}`,
      type: "document_request",
      kind: "document_request",
      activity_type: "document_request",
      title: row.title,
      label: row.title,
      description: row.requested_from_name
        ? `Requested from ${row.requested_from_name}${row.requested_from_role ? ` · ${row.requested_from_role}` : ""}`
        : (row.note || `Requested from ${row.requested_from_role || "participant"}`),
      actor_role: "Estate Agent",
      created_at: createdAt,
    };
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    overrides[dealId] = {
      ...prev,
      activity: [activityRow, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    // Reflect immediately in chainStore deal state.
    setDeals((arr) =>
      arr.map((x) => {
        if (x.id !== dealId) return x;
        return {
          ...x,
          activity: [activityRow, ...((x.activity) || [])],
          _pilotOverridden: true,
        };
      })
    );
    return row;
  }, []);

  // Returns the canonical pending+fulfilled doc request list for a deal
  // (seed + user-created, deduped by id). Read-only.
  const getPilotDocumentRequests = useCallback((dealId) => {
    return _docRequestsFor(dealId, loadPilotDocRequests());
  }, []);

  // Marks a pilot document request as fulfilled by a specific document
  // upload. The Documents Hub will then surface it under "Approved"
  // instead of "Pending Requests".
  //
  // iter-282 — Documents → Timeline automation.
  // When the doc-timeline-automation flag is enabled, this function
  // also (a) calls `markPilotJourneyStepComplete` for the mapped
  // journey step, (b) clears any matching blockers on the deal, and
  // (c) logs a calm "automation" activity row. The cascade is
  // idempotent: re-fulfilling the same request will not re-advance
  // the milestone or re-log activity.
  const fulfillPilotDocumentRequest = useCallback((dealId, requestId, docId = null) => {
    const fulfilledAt = new Date().toISOString();
    const all = loadPilotDocRequests();
    const list = Array.isArray(all[dealId]) ? all[dealId] : [];
    // iter-alignment · P54 — Doc requests are user-created only
    // (no seed layer).  If the request id isn't in the store,
    // there is nothing to fulfil.
    const userRow = list.find((r) => r.id === requestId);
    const previouslyFulfilled =
      String(userRow?.status || "").toLowerCase() === "fulfilled";
    let fulfilledRow = null;
    if (userRow) {
      const next = { ...userRow, status: "fulfilled", fulfilled_at: fulfilledAt, fulfilled_doc_id: docId };
      all[dealId] = list.map((r) => (r.id === requestId ? next : r));
      fulfilledRow = next;
    } else {
      return null;
    }
    savePilotDocRequests(all);

    // iter-282 — Trigger the documents → timeline cascade.
    let automation = null;
    if (isDocTimelineAutomationEnabled() && !previouslyFulfilled) {
      const deal = deals.find((d) => d.id === dealId);
      if (deal) {
        const plan = automationPlanFor({ deal, request: fulfilledRow });
        if (plan) {
          // (a) Advance the mapped journey step (also persists
          //     completedSteps + logs a milestone_completed activity
          //     via markPilotJourneyStepComplete).
          if (plan.stepToMarkComplete) {
            markPilotJourneyStepComplete(dealId, plan.stepToMarkComplete);
          }
          // (b) Clear matching blockers + (c) append the automation
          //     activity row to the override + live deal state.
          //     iter-285 — Decorate the automation row with the
          //     participant role when accountability is enabled so
          //     the activity feed groups by responsibility.
          const decoratedAutomationRow = plan.activityRow
            ? (isParticipantAccountabilityEnabled()
                ? decorateActivityWithParticipant(plan.activityRow, fulfilledRow)
                : plan.activityRow)
            : null;
          const overrides = loadPilotOverrides();
          const prev = overrides[dealId] || {};
          const liveBlockers = Array.isArray(deal.blockers) ? deal.blockers : [];
          const nextBlockers = liveBlockers.map((b) =>
            plan.blockersToClear.includes(b?.id)
              ? { ...b, status: "resolved", resolved_at: fulfilledAt, resolved_by: "automation" }
              : b
          );
          const nextActivity = [
            ...(decoratedAutomationRow ? [decoratedAutomationRow] : []),
            ...(prev.activity || []),
          ].slice(0, 50);
          overrides[dealId] = {
            ...prev,
            blockers: nextBlockers,
            activity: nextActivity,
          };
          savePilotOverrides(overrides);
          setDeals((arr) =>
            arr.map((x) => {
              if (x.id !== dealId) return x;
              const xBlockers = Array.isArray(x.blockers) ? x.blockers : [];
              return {
                ...x,
                blockers: xBlockers.map((b) =>
                  plan.blockersToClear.includes(b?.id)
                    ? { ...b, status: "resolved", resolved_at: fulfilledAt, resolved_by: "automation" }
                    : b
                ),
                activity: decoratedAutomationRow
                  ? [decoratedAutomationRow, ...((x.activity) || [])]
                  : (x.activity || []),
                _pilotOverridden: true,
              };
            })
          );
          automation = plan;
        }
      }
    }

    return { dealId, requestId, fulfilledAt, automation };
  }, [deals, markPilotJourneyStepComplete]);

  /* iter-284 — Mark a single request as chased at the given level.
   *
   * Appends a `{ level, sent_at }` entry to the request's
   * `chase_history` array, persists the user-row to localStorage
   * (shadowing the seed row if needed), and emits a calm
   * `doc_request_chased` activity row onto the deal so the
   * Documents Hub + Deal Room activity feeds reflect the chase.
   *
   * Idempotent: re-chasing the same level is a no-op (the
   * existing chase_history entry stays in place, no new
   * activity row is logged).
   */
  const markPilotRequestChased = useCallback((dealId, requestId, level, opts = {}) => {
    const sentAt = opts.sentAt || new Date().toISOString();
    const all = loadPilotDocRequests();
    const list = Array.isArray(all[dealId]) ? all[dealId] : [];
    // iter-alignment · P54 — user-created requests only.
    const userRow = list.find((r) => r.id === requestId);
    const sourceRow = userRow;
    if (!sourceRow) return null;
    const history = Array.isArray(sourceRow.chase_history) ? sourceRow.chase_history : [];
    // Idempotency — never double-log the same level.
    if (history.some((h) => Number(h.level) === Number(level))) {
      return { dealId, requestId, level, sentAt: history.find((h) => Number(h.level) === Number(level)).sent_at, idempotent: true };
    }
    const nextHistory = [...history, { level: Number(level), sent_at: sentAt }];
    const nextRow = { ...sourceRow, chase_history: nextHistory };
    if (userRow) {
      all[dealId] = list.map((r) => (r.id === requestId ? nextRow : r));
    } else {
      all[dealId] = [nextRow, ...list];
    }
    savePilotDocRequests(all);
    const baseRow = chaseActivityRowFor({ dealId, request: nextRow, level, sentAt });
    // iter-285 — Decorate the row with participant attribution when
    // accountability is enabled. The decorator is a pure no-op when
    // the request carries no resolvable owner.
    const activityRow = isParticipantAccountabilityEnabled()
      ? decorateActivityWithParticipant(baseRow, nextRow)
      : baseRow;
    const overrides = loadPilotOverrides();
    const prev = overrides[dealId] || {};
    overrides[dealId] = {
      ...prev,
      activity: [activityRow, ...(prev.activity || [])].slice(0, 50),
    };
    savePilotOverrides(overrides);
    setDeals((arr) =>
      arr.map((x) => (x.id !== dealId ? x : {
        ...x,
        activity: [activityRow, ...((x.activity) || [])],
        _pilotOverridden: true,
      }))
    );
    return { dealId, requestId, level, sentAt, activity: activityRow };
  }, []);

  /* iter-284 — Materialise pending chases across every pilot deal.
   *
   * Walks the canonical doc-request list for every deal, computes
   * the pending chase ladder per request, and emits any chases
   * whose `daysAfter` window has elapsed. Idempotent — repeated
   * calls never double-log because `markPilotRequestChased`
   * short-circuits when the level is already in chase_history.
   *
   * Called once on app load (useEffect below) so the operator
   * lands on a pre-warmed pilot with deterministic chase activity
   * already on record for At-Risk + Watch deals.
   */
  const materialisePendingChases = useCallback(() => {
    if (!isDocChasingEnabled()) return { dealsProcessed: 0, chasesLogged: 0 };
    const userMap = loadPilotDocRequests();
    let dealsProcessed = 0, chasesLogged = 0;
    // iter-alignment · P54 — Walk only user-created request ids
    // (no seed layer).  Idempotency still holds via
    // `markPilotRequestChased`'s chase_history check.
    const allDealIds = Object.keys(userMap);
    for (const dealId of allDealIds) {
      const list = _docRequestsFor(dealId, userMap);
      let dealHadChase = false;
      for (const request of list) {
        const { pending } = getChaseStateFor(request);
        for (const p of pending) {
          // Sent timestamp = the canonical due time (not "now") so
          // chase timestamps line up with the elapsed-day windows
          // and read consistently in the activity feed.
          markPilotRequestChased(dealId, request.id, p.level, { sentAt: p.dueAt });
          chasesLogged++;
          dealHadChase = true;
        }
      }
      if (dealHadChase) dealsProcessed++;
    }
    return { dealsProcessed, chasesLogged };
  }, [markPilotRequestChased]);

  /* iter-284 — Materialise pending chases on app load.
   *
   * Runs ONCE when the provider mounts (and again when the user
   * signs in) so the operator's first paint of the pilot shows
   * realistic, deterministic chase activity already on record.
   *
   * Flag-gated by `isDocChasingEnabled()` inside the action
   * itself, so when the flag is off this is a calm no-op. */
  useEffect(() => {
    if (!user) return;
    if (!isDocChasingEnabled()) return;
    materialisePendingChases();
  }, [user, materialisePendingChases]);

  const inviteContact = useCallback(async (dealId, contactId, status = "Invited") => {
    const deal = deals.find((d) => d.id === dealId);
    const next = { ...(deal?.invitations || {}), [contactId]: status };
    return updateDeal(dealId, { invitations: next });
  }, [deals, updateDeal]);

  const addReaction = useCallback((dealId, msgId, emoji, userId) => {
    // Optimistic local-only — reactions aren't persisted server-side yet.
    setDeals((arr) =>
      arr.map((d) => {
        if (d.id !== dealId) return d;
        return {
          ...d,
          messages: d.messages.map((m) => {
            if (m.id !== msgId) return m;
            const reactions = { ...(m.reactions || {}) };
            const list = new Set(reactions[emoji] || []);
            if (list.has(userId)) list.delete(userId);
            else list.add(userId);
            if (list.size === 0) delete reactions[emoji];
            else reactions[emoji] = Array.from(list);
            return { ...m, reactions };
          }),
        };
      })
    );
  }, []);

  const toggleFavourite = useCallback((dealId) => {
    setFavourites((s) => {
      const next = new Set(s);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  }, []);

  const addDocument = useCallback(async (dealId, doc) => {
    const deal = deals.find((d) => d.id === dealId);
    if (deal?._v2only) {
      // v2-only deals: register metadata via /api/v2 (no binary upload yet —
      // Supabase Storage wiring is out of scope this turn).
      const { data } = await api.post(`/api/v2/deals/${dealId}/documents`, {
        folder: doc.folder || doc.category || "Miscellaneous",
        name: doc.name || "Untitled document",
        storage_path: doc.storage_path || null,
        size_bytes: doc.size_bytes || doc.size || 0,
        mime_type: doc.mime_type || doc.type || null,
      });
      const v2Doc = {
        id: data.id, deal_id: data.deal_id, name: data.name,
        folder: data.folder || "Miscellaneous", category: data.folder || null,
        storage_path: data.storage_path, size_bytes: data.size_bytes || 0,
        size: data.size_bytes || 0, mime_type: data.mime_type, type: data.mime_type,
        uploaded_by: data.uploaded_by_id || null, created_at: data.created_at, _src: "v2",
      };
      // iter-282 — Pipe v2 uploads through the same automation.
      _maybeAutomateDocUpload(dealId, v2Doc);
      return v2Doc;
    }
    const full = { id: id("d"), at: Date.now(), ...doc };
    const nextDocs = [full, ...((deal?.documents) || [])];
    const { data } = await api.patch(`/api/deals/${dealId}`, { documents: nextDocs });
    const refreshed = dealFromApi(data);
    setDeals((arr) => arr.map((d) => (d.id === dealId ? refreshed : d)));
    // Also log activity entry server-side.
    api.post(`/api/deals/${dealId}/activity`, { text: `Uploaded ${doc.name || "document"}`, kind: "doc" })
      .then(({ data }) => {
        const r2 = dealFromApi(data);
        setDeals((arr) => arr.map((d) => (d.id === dealId ? r2 : d)));
      })
      .catch(() => { /* noop */ });
    // iter-282 — Trigger documents → timeline automation.
    _maybeAutomateDocUpload(dealId, full);
    return full;
  }, [deals]);

  /* iter-282 — Shared automation entry point used by both the
   * pilot + v2 upload paths. Looks for a pending doc request on
   * the deal that matches the upload (by template_id, by
   * doc_label / name similarity), and — when one is found —
   * auto-calls `fulfillPilotDocumentRequest` to fire the full
   * Documents → Timeline cascade (advance milestone, clear
   * matching blockers, log activity). When no pending request
   * matches but the upload's name itself maps to a known
   * template, fires a silent step-advance directly. */
  const _maybeAutomateDocUpload = useCallback((dealId, uploadedDoc) => {
    if (!isDocTimelineAutomationEnabled()) return;
    const deal = deals.find((d) => d.id === dealId);
    if (!deal) return;
    const requests = _docRequestsFor(dealId, loadPilotDocRequests());
    const pending = requests.filter((r) => String(r?.status || "").toLowerCase() === "pending");
    // 1) Look for a pending request whose template_id matches.
    let match = pending.find((r) => uploadedDoc?.template_id
                                  && r.template_id === uploadedDoc.template_id);
    // 2) Fall back to doc_label / name similarity.
    if (!match) {
      const nameLower = String(uploadedDoc?.name || "").toLowerCase();
      match = pending.find((r) => {
        const label = String(r?.doc_label || r?.title || "").toLowerCase();
        if (!label || !nameLower) return false;
        const tokens = label.split(/[^a-z]+/).filter((t) => t.length >= 4);
        return tokens.some((t) => nameLower.includes(t));
      });
    }
    if (match) {
      // Auto-fulfil the request — fires the cascade.
      fulfillPilotDocumentRequest(dealId, match.id, uploadedDoc?.id || null);
      return;
    }
    // 3) No matching request — try direct step inference from the
    //    uploaded filename. Builds an ephemeral request shape so
    //    the automation engine can plan side-effects.
    const ephemeral = {
      id:           `auto-upload-${dealId}-${Date.now()}`,
      template_id:  uploadedDoc?.template_id || null,
      doc_label:    uploadedDoc?.name || null,
    };
    const plan = automationPlanFor({ deal, doc: ephemeral });
    if (!plan) return;
    if (plan.stepToMarkComplete) {
      markPilotJourneyStepComplete(dealId, plan.stepToMarkComplete);
    }
    // Append a calm automation activity row.
    if (plan.activityRow) {
      const overrides = loadPilotOverrides();
      const prev = overrides[dealId] || {};
      overrides[dealId] = {
        ...prev,
        activity: [plan.activityRow, ...(prev.activity || [])].slice(0, 50),
      };
      savePilotOverrides(overrides);
      setDeals((arr) =>
        arr.map((x) => (x.id !== dealId ? x : {
          ...x,
          activity: [plan.activityRow, ...((x.activity) || [])],
          _pilotOverridden: true,
        }))
      );
    }
  }, [deals, fulfillPilotDocumentRequest, markPilotJourneyStepComplete]);

  /* iter-336/Δ — Ref pattern: `appendPilotDocument` and
   * `replacePilotDocument` are memoised with `[]` deps (they are
   * created once for the provider's lifetime) so they capture
   * the FIRST render's `_maybeAutomateDocUpload`, which in turn
   * closed over a stale `deals`. Routing through this ref keeps
   * the cascade reading the LATEST `_maybeAutomateDocUpload`
   * closure (re-bound on every render where its deps change) so
   * the Documents → Milestone automation fires correctly on
   * uploads / replacements made by the operator long after the
   * initial mount. */
  useEffect(() => {
    _automateDocUploadRef.current = _maybeAutomateDocUpload;
  }, [_maybeAutomateDocUpload]);

  const sendMessage = useCallback(async (dealId, msg) => {
    const current = deals.find((x) => x.id === dealId);
    if (current?._v2only) {
      const { data } = await api.post(`/api/v2/deals/${dealId}/messages`, {
        body: msg.body || "",
        internal: !!msg.internal,
      });
      const newMsg = {
        id: data.id, body: data.body, text: data.body,
        from: data.author_id || null, internal: !!data.internal,
        at: data.created_at, _src: "v2",
      };
      const updated = { ...current, messages: [...(current.messages || []), newMsg] };
      setDeals((arr) => arr.map((d) => (d.id === dealId ? updated : d)));
      return newMsg;
    }
    const { data } = await api.post(`/api/deals/${dealId}/messages`, {
      body: msg.body || "",
      internal: !!msg.internal,
    });
    const refreshed = dealFromApi(data);
    setDeals((arr) => arr.map((d) => (d.id === dealId ? refreshed : d)));
    return refreshed.messages[refreshed.messages.length - 1];
  }, [deals]);

  // ─── v2-only write helpers ───────────────────────────────────────────────
  const addActivity = useCallback(async (dealId, { activity_type, title, description, meta } = {}) => {
    if (!dealId || !activity_type || !title) return null;
    const { data } = await api.post(`/api/v2/deals/${dealId}/activity`, {
      activity_type, title,
      description: description || null,
      meta: meta || {},
    });
    setDeals((arr) => arr.map((d) => {
      if (d.id !== dealId) return d;
      const kindMap = {
        document_upload: "doc", document_request: "doc", message: "msg",
        risk_flagged: "risk", risk_cleared: "risk", milestone_completed: "milestone",
        participant_added: "participant", participant_removed: "participant",
        note: "note", task_created: "task", task_completed: "task",
        status_change: "stage", stage_change: "stage", system: "system",
      };
      const newEntry = {
        id: data.id, kind: kindMap[data.activity_type] || data.activity_type || "system",
        at: data.created_at, actor: data.created_by_id || null,
        text: data.title, description: data.description || null, meta: data.meta || {}, _src: "v2",
      };
      return { ...d, activity: [newEntry, ...(d.activity || [])] };
    }));
    return data;
  }, []);

  const addParticipant = useCallback(async (dealId, { participant_type, full_name, email, phone, organisation_name } = {}) => {
    if (!dealId || !participant_type || !full_name) return null;
    const { data } = await api.post(`/api/v2/deals/${dealId}/participants`, {
      participant_type, full_name,
      email: email || null,
      phone: phone || null,
      organisation_name: organisation_name || null,
    });
    return data;
  }, []);

  const findContact  = useCallback((cid) => contacts.find((c) => c.id === cid) || sampleContacts.find((c) => c.id === cid),   [contacts]);
  // iter-alignment · P54 — Live API SSOT.  Resolve from in-memory
  // state (which mirrors `/api/properties` and `/api/deals`).  No
  // localStorage fallback — the API IS the truth.
  const findProperty = useCallback((pid) => {
    if (!pid) return null;
    return properties.find((p) => p.id === pid) || null;
  }, [properties]);

  /* iter-alignment · P3 · Contact Profile · Live Transaction
   * Property Data (2026-02-28)
   * ----------------------------------------------------------------
   * The initial `/api/properties` fetch is capped at 200 rows for
   * bandwidth.  On surfaces that render a Deal Room's linked
   * Property (Contact Profile "Live Transactions", Deep-link
   * Property Hero, Forecasting Centre chip rows) the Property may
   * be beyond that cap — the local list would return `null` and
   * the UI would fall through to a generic "Property" label.
   *
   * `ensurePropertyLoaded` closes that gap: it looks up the id in
   * the local `properties` state first (no network on hits) and
   * only fires a single `/api/properties/{pid}` GET when the row is
   * missing.  The response is merged into the store so every
   * consumer re-renders with the real address + thumbnail on the
   * next tick.  Idempotent: a second call with the same id while
   * an in-flight fetch is running dedupes via `_ensureInflight`
   * (a plain `Map` — not React state — because these are pure
   * network side-effects that must NOT trigger re-renders).
   */
  const _ensureInflightRef = useRef(new Map());
  const ensurePropertyLoaded = useCallback(async (pid) => {
    if (!pid || typeof pid !== "string") return null;
    const cached = properties.find((p) => p.id === pid);
    if (cached) return cached;
    const inflight = _ensureInflightRef.current.get(pid);
    if (inflight) return inflight;
    const request = (async () => {
      try {
        const { data } = await api.get(`/api/properties/${pid}`);
        const fresh = propertyFromApi(data);
        setProperties((arr) => {
          // Only insert if not already present — avoids a
          // duplicate row when the effect races with a
          // refreshWorkspace().
          if (arr.some((x) => x.id === fresh.id)) return arr;
          return [...arr, fresh];
        });
        return fresh;
      } catch (_e) {
        return null;
      } finally {
        _ensureInflightRef.current.delete(pid);
      }
    })();
    _ensureInflightRef.current.set(pid, request);
    return request;
  }, [properties]);
  const findDeal = useCallback((did) => {
    if (!did) return null;
    // iter-304 — Always normalise the returned deal so consumers
    // (PropertyHero Timeline card, Forecasting Centre, Deal Rooms
    // list metadata) read a consistent shape.
    const inState = deals.find((d) => d.id === did);
    return inState ? normaliseDealDates(inState) : null;
  }, [deals]);

  // ─── Linking lookups ───────────────────────────────────────────────────────
  const dealsForContact = useCallback(
    (cid) => deals.filter((d) =>
      d.buyerId === cid ||
      d.sellerId === cid ||
      d.agentId === cid ||
      d.solicitorId === cid ||
      d.brokerId === cid ||
      d.buyerSolicitorId === cid ||
      d.sellerSolicitorId === cid ||
      // iter-alignment · P51 — Contacts SSOT: also match via the
      // aggregated participant_contact_ids array so contacts added
      // via non-FK slots (progressor / other / freshly-added via
      // POST /deals/{id}/participants) still show up on the
      // Contact Profile's Active Transactions rail.
      (Array.isArray(d.participantContactIds) && d.participantContactIds.includes(cid))
    ),
    [deals]
  );
  const dealsForProperty = useCallback((pid) => deals.filter((d) => d.propertyId === pid), [deals]);

  // ─── iter-290 · Property archive ledger ───────────────────────
  // Client-side archive set persisted in localStorage. Archive
  // hides the property from active surfaces (Properties list,
  // Forecasting Centre, Deal Rooms list, Documents Centre, KPI
  // counts). Direct URLs still resolve. Deal Rooms / Documents /
  // Activity / Participants / Revenue stay intact (no mutation).
  const [archivedPropertyIds, setArchivedPropertyIds] = useState(
    () => new Set(loadArchivedPropertyIds()),
  );
  const isPropertyArchived = useCallback((pid) => {
    return !!pid && archivedPropertyIds.has(pid);
  }, [archivedPropertyIds]);
  // iter-290 — Helper that records an archive/restore event on every
  // deal linked to a property. For pilot deals, prepends into the
  // pilot-override `activity` array (same envelope iter-282/284 used
  // for milestone + chase events). For non-pilot deals, fires a calm
  // best-effort `addActivity` v2 call — failure is silent so the
  // archive action stays instant either way. Always also mirrors
  // into the in-memory deal so the Activity feed updates without
  // a refetch.
  const recordPropertyArchiveActivity = useCallback((pid, action) => {
    if (!pid || !["archive", "restore"].includes(action)) return;
    const nowIso = new Date(Date.now()).toISOString();
    const linkedDeals = (deals || []).filter(
      (d) => (d.propertyId || d.property_id) === pid,
    );
    if (linkedDeals.length === 0) return;
    const verb = action === "archive" ? "archived" : "restored";
    const description = action === "archive"
      ? "Property archived · hidden from active views"
      : "Property restored · returned to active views";

    // ── Pilot deals: persist via pilot-override layer ──
    const overrides = loadPilotOverrides();
    let overridesDirty = false;
    setDeals((arr) => arr.map((d) => {
      if (!linkedDeals.find((x) => x.id === d.id)) return d;
      const row = {
        id: `arc-${action}-${d.id}-${Date.now()}`,
        ts: nowIso,
        actor: "Operator",
        kind: `property_${action}`,
        title: `Property ${verb}`,
        description,
      };
      // Mirror into provider state immediately.
      const baseActivity = Array.isArray(d.activity) ? d.activity : [];
      const next = { ...d, activity: [row, ...baseActivity] };

      if (d._pilot) {
        const prev = overrides[d.id] || {};
        const prevAct = Array.isArray(prev.activity) ? prev.activity : [];
        overrides[d.id] = { ...prev, activity: [row, ...prevAct] };
        overridesDirty = true;
      } else {
        // Best-effort v2 mirror for live deals. Fire-and-forget.
        try {
          api.post(`/api/v2/deals/${d.id}/activity`, {
            kind: `property_${action}`,
            title: `Property ${verb}`,
            description,
          }).catch(() => { /* silent */ });
        } catch (_e) { /* silent */ }
      }
      return next;
    }));
    if (overridesDirty) savePilotOverrides(overrides);
  }, [deals]);

  const archiveProperty = useCallback((pid) => {
    if (!pid || archivedPropertyIds.has(pid)) return false;
    const next = new Set(archivedPropertyIds);
    next.add(pid);
    saveArchivedPropertyIds(Array.from(next));
    setArchivedPropertyIds(next);
    recordPropertyArchiveActivity(pid, "archive");
    return true;
  }, [archivedPropertyIds, recordPropertyArchiveActivity]);
  const restoreProperty = useCallback((pid) => {
    if (!pid || !archivedPropertyIds.has(pid)) return false;
    const next = new Set(archivedPropertyIds);
    next.delete(pid);
    saveArchivedPropertyIds(Array.from(next));
    setArchivedPropertyIds(next);
    recordPropertyArchiveActivity(pid, "restore");
    return true;
  }, [archivedPropertyIds, recordPropertyArchiveActivity]);
  const isDealArchivedByProperty = useCallback(
    (deal) => isPropertyArchived(deal?.propertyId || deal?.property_id),
    [isPropertyArchived],
  );

  // ─── Tasks (server-backed) ────────────────────────────────────────────────
  // In the UI: { id, dealId, title, assignedTo, due (ms), priority, status, notes, completedAt (ms) }
  // On the wire: { id, deal_id, title, assigned_to, due_at (iso), priority, status, notes, completed_at (iso) }
  const taskFromApi = (t) => ({
    id: t.id,
    dealId: t.deal_id || null,
    title: t.title,
    assignedTo: t.assigned_to || null,
    due: t.due_at ? Date.parse(t.due_at) : null,
    priority: t.priority || "Medium",
    status: t.status || "open",
    notes: t.notes || "",
    completedAt: t.completed_at ? Date.parse(t.completed_at) : null,
  });
  const taskToApi = (t) => {
    const out = {};
    if (t.dealId !== undefined)     out.deal_id = t.dealId;
    if (t.title !== undefined)      out.title = t.title;
    if (t.assignedTo !== undefined) out.assigned_to = t.assignedTo;
    if (t.due !== undefined)        out.due_at = t.due ? new Date(t.due).toISOString() : null;
    if (t.priority !== undefined)   out.priority = t.priority;
    if (t.status !== undefined)     out.status = t.status;
    if (t.notes !== undefined)      out.notes = t.notes;
    return out;
  };

  const notifFromApi = (n) => ({
    id: n.id,
    kind: n.kind,
    dealId: n.deal_id || null,
    title: n.title || null,
    body: n.body,
    subtitle: n.subtitle || null,
    category: n.category || null,
    categoryLabel: n.category_label || null,
    iconKey: n.icon_key || null,
    priority: n.priority || null,
    deepLink: n.deep_link || (n.deal_id ? `/deal-rooms/${n.deal_id}` : null),
    at: n.created_at ? Date.parse(n.created_at) : Date.now(),
    read: !!n.read,
  });

  const viewingFromApi = (v) => ({
    id: v.id,
    dealId: v.deal_id,
    buyerId: v.buyer_id || null,
    when: v.when_at ? Date.parse(v.when_at) : Date.now(),
    agent: v.agent_id || null,
    status: v.status,
    outcome: v.outcome || null,
    feedback: v.feedback || null,
  });
  const viewingToApi = (v) => {
    const out = {};
    if (v.dealId !== undefined)  out.deal_id = v.dealId;
    if (v.buyerId !== undefined) out.buyer_id = v.buyerId;
    if (v.agent !== undefined)   out.agent_id = v.agent;
    if (v.when !== undefined)    out.when_at = v.when ? new Date(v.when).toISOString() : new Date().toISOString();
    if (v.status !== undefined)  out.status = v.status;
    if (v.outcome !== undefined) out.outcome = v.outcome;
    if (v.feedback !== undefined) out.feedback = v.feedback;
    return out;
  };

  // Hydrate workspace data (tasks/notifications/viewings) when user changes.
  useEffect(() => {
    if (!user) {
      setTasks([]); setNotifications([]); setViewings([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      api.get("/api/tasks").catch(() => ({ data: [] })),
      api.get("/api/notifications").catch(() => ({ data: [] })),
      api.get("/api/viewings").catch(() => ({ data: [] })),
    ]).then(([t, n, v]) => {
      if (cancelled) return;
      setTasks((Array.isArray(t.data) ? t.data : []).map(taskFromApi));
      setNotifications((Array.isArray(n.data) ? n.data : []).map(notifFromApi));
      setViewings((Array.isArray(v.data) ? v.data : []).map(viewingFromApi));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.org_id]);

  const addTask = useCallback(async (task) => {
    const payload = taskToApi({ priority: "Medium", status: "open", ...task });
    const { data } = await api.post("/api/tasks", payload);
    const t = taskFromApi(data);
    setTasks((arr) => [t, ...arr]);
    return t;
  }, []);
  const toggleTask = useCallback(async (tid) => {
    const current = tasks.find((t) => t.id === tid);
    if (!current) return;
    const next = current.status === "done" ? "open" : "done";
    const { data } = await api.patch(`/api/tasks/${tid}`, { status: next });
    const t = taskFromApi(data);
    setTasks((arr) => arr.map((x) => (x.id === tid ? t : x)));
  }, [tasks]);
  const updateTask = useCallback(async (tid, patch) => {
    const { data } = await api.patch(`/api/tasks/${tid}`, taskToApi(patch || {}));
    const t = taskFromApi(data);
    setTasks((arr) => arr.map((x) => (x.id === tid ? t : x)));
    return t;
  }, []);
  const deleteTask = useCallback(async (tid) => {
    await api.delete(`/api/tasks/${tid}`);
    setTasks((arr) => arr.filter((t) => t.id !== tid));
  }, []);

  // ─── Notifications (server-backed) ────────────────────────────────────────
  const markNotifRead = useCallback(async (nid) => {
    setNotifications((arr) => arr.map((n) => (n.id === nid ? { ...n, read: true } : n)));
    try { await api.post(`/api/notifications/${nid}/read`); } catch { /* noop */ }
  }, []);
  const markAllNotifRead = useCallback(async () => {
    setNotifications((arr) => arr.map((n) => ({ ...n, read: true })));
    try { await api.post("/api/notifications/read-all"); } catch { /* noop */ }
  }, []);
  const addNotification = useCallback(async (n) => {
    const { data } = await api.post("/api/notifications", {
      kind: n.kind || "info",
      body: n.body || "",
      deal_id: n.dealId || null,
      user_id: n.userId || null,
    });
    const out = notifFromApi(data);
    setNotifications((arr) => [out, ...arr]);
    // Bridge to native push dispatcher (no-op if permission missing or category muted)
    try {
      const mod = await import("../lib/notify");
      const KIND_TO_CATEGORY = {
        risk: "risk", overdue: "overdue", milestone: "milestone",
        document: "document", reply: "reply", mention: "mention",
      };
      const category = KIND_TO_CATEGORY[out.kind] || "reply";
      mod.notify({
        title: n.title || "ChainFlow",
        body: out.body,
        dealId: out.dealId || n.dealId || null,
        category,
        url: n.url,
      });
    } catch { /* noop */ }
    return out;
  }, []);

  // ─── Viewings (server-backed) ─────────────────────────────────────────────
  const addViewing = useCallback(async (v) => {
    const payload = viewingToApi({ status: "Confirmed", ...v });
    const { data } = await api.post("/api/viewings", payload);
    const out = viewingFromApi(data);
    setViewings((arr) => [out, ...arr]);
    return out;
  }, []);
  const updateViewing = useCallback(async (vid, patch) => {
    const { data } = await api.patch(`/api/viewings/${vid}`, viewingToApi(patch || {}));
    const out = viewingFromApi(data);
    setViewings((arr) => arr.map((v) => (v.id === vid ? out : v)));
    return out;
  }, []);
  const deleteViewing = useCallback(async (vid) => {
    await api.delete(`/api/viewings/${vid}`);
    setViewings((arr) => arr.filter((v) => v.id !== vid));
  }, []);

  const toggleFavDoc = useCallback((docId) => {
    setFavDocs((s) => {
      const next = new Set(s);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  // ─── Reset (for demos / tests) ─────────────────────────────────────────────
  const resetStore = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* noop */ }
    setTasks([]);
    setNotifications([]);
    setViewings([]);
    setFavourites(initial.favourites);
    setFavDocs(initial.favDocs);
  }, [initial]);

  const value = useMemo(
    () => ({
      // collections
      contacts, contactsLoading, contactsError,
      properties, propertiesLoading,
      deals, dealsLoading, dealsHydrated,
      tasks, notifications, viewings, favourites, favDocs,
      // schema
      stages: DEAL_STAGES,
      contactRoles: CONTACT_ROLES,
      contactStatuses: CONTACT_STATUS,
      propertyStatuses: PROPERTY_STATUS,
      dealStatuses: DEAL_STATUS,
      riskLevels: RISK_LEVEL,
      tenures: TENURES,
      folders: FOLDERS,
      recent: recentRef.current,
      // contacts
      addContact, updateContact, deleteContact, findContact, refreshContacts,
      // properties
      addProperty, updateProperty, deleteProperty, findProperty,
      // iter-alignment · P3 · Contact Profile · Live Transaction
      // Property Data (2026-02-28) — on-demand property loader
      // used by any surface that renders a Deal Room's linked
      // property beyond the initial 200-row list cap.
      ensurePropertyLoaded,
      // deals
      addDeal, updateDeal, deleteDeal, advanceStage, findDeal,
      archiveDeal, restoreDeal, patchDealLocally,
      // iter-318 — inline milestone editing
      patchMilestoneState,
      // iter-270F — pilot offline mark-complete helper
      markPilotJourneyStepComplete,
      // iter-297 — pilot offline document upload helper
      appendPilotDocument,
      // iter-336 — pilot offline document remove + replace helpers
      removePilotDocument, replacePilotDocument,
      // iter-302 — pilot offline activity logger (Quick Edit sheet)
      appendPilotActivity,
      // iter-270I — pilot document request lifecycle helpers
      createPilotDocumentRequest, getPilotDocumentRequests, fulfillPilotDocumentRequest,
      // iter-284 — pilot chasing helpers (T+3 / T+5 / T+7)
      markPilotRequestChased, materialisePendingChases,
      // workspace hydration + canonical operational-event helper
      hydrateDeals, refreshWorkspace, logOperationalEvent,
      // deal collaboration
      inviteContact, addDocument, sendMessage, addReaction, toggleFavourite,
      // v2 write helpers
      addActivity, addParticipant,
      // tasks / notifs / viewings
      addTask, toggleTask, updateTask, deleteTask,
      markNotifRead, markAllNotifRead, addNotification,
      addViewing, updateViewing, deleteViewing,
      toggleFavDoc,
      // linking
      dealsForContact, dealsForProperty,
      // iter-290 · Property archive (pilot client-side)
      archivedPropertyIds, isPropertyArchived, archiveProperty, restoreProperty,
      isDealArchivedByProperty,
      // misc
      resetStore,
    }),
    [
      contacts, contactsLoading, contactsError,
      properties, propertiesLoading,
      deals, dealsLoading, dealsHydrated,
      tasks, notifications, viewings, favourites, favDocs,
      addContact, updateContact, deleteContact, findContact, refreshContacts,
      addProperty, updateProperty, deleteProperty, findProperty,
      ensurePropertyLoaded,
      addDeal, updateDeal, deleteDeal, advanceStage, findDeal,
      archiveDeal, restoreDeal,
      patchDealLocally,
      patchMilestoneState,
      markPilotJourneyStepComplete,
      appendPilotDocument,
      removePilotDocument, replacePilotDocument,
      appendPilotActivity,
      createPilotDocumentRequest, getPilotDocumentRequests, fulfillPilotDocumentRequest,
      markPilotRequestChased, materialisePendingChases,
      hydrateDeals, refreshWorkspace,
      logOperationalEvent,
      inviteContact, addDocument, sendMessage, addReaction, toggleFavourite,
      addActivity, addParticipant,
      addTask, toggleTask, updateTask, deleteTask,
      markNotifRead, markAllNotifRead, addNotification,
      addViewing, updateViewing, deleteViewing, toggleFavDoc,
      dealsForContact, dealsForProperty,
      archivedPropertyIds, isPropertyArchived, archiveProperty, restoreProperty,
      isDealArchivedByProperty,
      resetStore,
    ]
  );

  return <ChainStoreContext.Provider value={value}>{children}</ChainStoreContext.Provider>;
};

// ─── Tiny global event bus for triggering modals from anywhere ───────────────
export const cfBus = {
  emit: (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail })),
  on: (name, fn) => {
    const handler = (e) => fn(e.detail);
    window.addEventListener(name, handler);
    return () => window.removeEventListener(name, handler);
  },
};

export const openAddContact   = (preset) => cfBus.emit("cf:open-contact",  preset || {});
export const openEditContact  = (contact) => cfBus.emit("cf:open-contact",  { ...contact, _editing: true });
export const openAddProperty  = (preset) => cfBus.emit("cf:open-property", preset || {});
/* iter-alignment · P0 · Property Edit — Save Failure Trace
 * (2026-02-28) — Guard the module-level `openEditProperty` bus
 * event against stale / demo objects: refuse to open unless
 * `property?.id` is a non-empty string.  The Drawer's PATCH
 * would 404 with "That property could not be found in your
 * workspace." if we opened it on a payload whose `id` is
 * undefined / null / an empty string.  Backend org-isolation
 * checks are untouched — this guard just prevents the drawer
 * from firing a doomed request in the first place. */
export const openEditProperty = (property) => {
  const pid = property?.id;
  if (typeof pid !== "string" || pid.trim() === "") {
    if (typeof process !== "undefined" && process?.env?.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[openEditProperty] refused — no canonical id on payload", property);
    }
    return;
  }
  cfBus.emit("cf:open-property", { ...property, _editing: true });
};
export const openCreateDeal   = (preset) => cfBus.emit("cf:open-deal",     preset || {});
export const openEditDeal     = (deal)   => cfBus.emit("cf:open-edit-deal", deal);
export const openNotifications = () => cfBus.emit("cf:open-notifications");
export const openGlobalSearch  = () => cfBus.emit("cf:open-search");
